import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "@/App";
import { getLeadScore } from "@/lib/getLeadScore";
import axios from "axios";
import { toast } from "sonner";
import {
  CalendarCheck, Plus, ChevronDown, ChevronLeft, ChevronRight, Check, X,
  Search, Eye, Clock, AlertTriangle, Phone, PhoneOff, Mail, FileText, Users,
  Loader2, MoreHorizontal, Send, Calendar, BarChart3, Target, User,
  Settings as SettingsIcon, Copy, Lock, Upload, Sparkles
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import LeadFilterPanel, { applyLeadFilters } from "@/components/LeadFilterPanel";
import { applyLocationFilters, applyLastContactFilter } from "@/lib/leadFilterConfig";
import ScheduleMeetingModal from "@/components/ScheduleMeetingModal";
import ClientDemoBookingModal from "@/components/ehi/ClientDemoBookingModal";
import EhiDemoOutcomeModal, { demoClientId } from "@/components/ehi/EhiDemoOutcomeModal";
import { DispositionModal } from "@/pages/SalesDialerLive";
import { useGlobalLeadFilters } from "@/lib/globalLeadFilters";
import { SlidersHorizontal } from "lucide-react";

/* ── Helpers ── */
const fmt12 = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  if (isNaN(h)) return t;
  const suffix = h >= 12 ? "PM" : "AM";
  const hr = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hr}:${String(m || 0).padStart(2, "0")} ${suffix}`;
};
const fmtDate = (d) => {
  if (!d) return "";
  try {
    const dt = new Date(d + "T00:00:00");
    return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return d; }
};
const demoName = (d) => d.company || d.lead_title || d.name || d.contact_name || "Unknown";
const demoContact = (d) => d.contact_name || d.name || "—";
const trunc = (s, n) => s && s.length > n ? s.slice(0, n) + "…" : s || "";

const ROLE_OPTIONS = [
  { id: "super_admin", label: "Super Admin", desc: "Full access to everything", bg: "#FCEBEB", color: "#A32D2D", initials: "SA" },
  { id: "admin", label: "Admin", desc: "Manage users, settings, approvals", bg: "#FAEEDA", color: "#854F0B", initials: "AD" },
  { id: "sales_manager", label: "Sales Manager", desc: "Team oversight + approvals", bg: "#E1F5EE", color: "#0F6E56", initials: "SM" },
  { id: "sales_agent", label: "Sales Agent", desc: "Own leads and demos only", bg: "#E6F1FB", color: "#185FA5", initials: "SA" },
  { id: "bookings_agent", label: "Bookings Agent", desc: "Schedule and manage demos", bg: "#EEEDFE", color: "#3C3489", initials: "BA" },
  { id: "contracts_agent", label: "Contracts Agent", desc: "Quote and contract approvals only", bg: "#F1EFE8", color: "#5F5E5A", initials: "CA" },
];
const STATUS_COLORS = {
  confirmed: { bg: "#EAF3DE", color: "#3B6D11", border: "#1D9E75", label: "Confirmed" },
  unconfirmed: { bg: "#FAEEDA", color: "#854F0B", border: "#BA7517", label: "Unconfirmed" },
  completed: { bg: "#f5f5f5", color: "#888", border: "#888", label: "Completed" },
  cancelled: { bg: "#FCEBEB", color: "#A32D2D", border: "#E24B4A", label: "Cancelled" },
  // Alias: the Outlook cancel webhook + /api/meetings cascade both set
  // `status: "canceled"` (single L). Without this alias the badge
  // falls through to the default "Confirmed" green — the exact bug
  // reported ("Canceled booking still shows Confirmed"). Keep the
  // spelling variant separate so the "All Demos" filter dropdown
  // (built from Object.entries) still only surfaces the canonical
  // list, but any doc with either spelling renders correctly.
  canceled: { bg: "#FCEBEB", color: "#A32D2D", border: "#E24B4A", label: "Canceled", _alias: true },
  no_show: { bg: "#FCEBEB", color: "#A32D2D", border: "#E24B4A", label: "No-Show" },
  rescheduled: { bg: "#FAEEDA", color: "#854F0B", border: "#BA7517", label: "Rescheduled" },
};
const OUTCOME_OPTIONS = [
  { id: "highly_interested", label: "Highly interested", sub: "Ready to move forward", bg: "#EAF3DE", color: "#3B6D11" },
  { id: "requested_proposal", label: "Requested proposal", sub: "Wants a formal quote", bg: "#E6F1FB", color: "#185FA5" },
  { id: "needs_followup", label: "Needs follow-up", sub: "Positive but not decided", bg: "#E1F5EE", color: "#0F6E56" },
  { id: "needs_info", label: "Needs more info", sub: "Has questions or concerns", bg: "#FAEEDA", color: "#854F0B" },
  { id: "not_a_fit", label: "Not a fit", sub: "Product doesn't meet needs", bg: "#FCEBEB", color: "#A32D2D" },
  { id: "no_show", label: "No-show", sub: "Did not attend", bg: "#FCEBEB", color: "#A32D2D" },
];
// Iter 251 — /demo-bookings now uses the SHARED disposition modal (same as
// /lead-focus + Sales Dialer). The rep picks Stage=Demo → "Demo Completed"
// tier sub-status; we map that sub-status → the legacy demo outcome enum so
// the booking is still marked complete (clears the awaiting-outcome queue,
// cascades the meetings "completed" counter, and auto-creates follow-ups).
const SUBSTATUS_TO_DEMO_OUTCOME = {
  "Highly Interested": "highly_interested",
  "Requested Proposal": "requested_proposal",
  "Needs Follow-up": "needs_followup",
  "Need More Info": "needs_info",
  "Not a Fit": "not_a_fit",
  "Demo No-Show": "no_show",
};
const TABS = [
  { id: "overview", label: "Overview" },
  { id: "calendar", label: "Calendar" },
  { id: "all-demos", label: "All Demos" },
  { id: "demo-pipeline", label: "Demo Pipeline" },
  { id: "team-schedule", label: "Team Schedule" },
  { id: "live-meeting", label: "Live Meeting" },
  { id: "analytics", label: "Analytics" },
  { id: "settings", label: "Settings" },
];
const SL = { fontSize: 11, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: "0.4px" };
const PRODUCTS_LIST = [
  { id: "ehr", name: "AI-powered EHR (MDNet)", category: "EHR & Practice Management" },
  { id: "billing", name: "Medical Billing + RevQ", category: "Revenue Cycle" },
  { id: "ccm", name: "CCM / Chronic Care Management", category: "Care Coordination" },
  { id: "rpm", name: "RPM / Remote Patient Monitoring", category: "Care Coordination" },
  { id: "pm", name: "Practice Management", category: "EHR & Practice Management" },
  { id: "rcs", name: "Revenue Cycle Service", category: "Revenue Cycle" },
];

function StatusBadge({ status }) {
  const cfg = STATUS_COLORS[status] || STATUS_COLORS.confirmed;
  return <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 500, background: cfg.bg, color: cfg.color }}>{cfg.label}</span>;
}

/* ── Modal Shell ── */
function Modal({ children, onClose, width = 580 }) {
  useEffect(() => { const k = (e) => { if (e.key === "Escape") onClose(); }; window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [onClose]);
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 12, width, maxWidth: "92vw", border: "0.5px solid #eee", maxHeight: "90vh", overflow: "auto" }} onClick={e => e.stopPropagation()}>{children}</div>
    </div>
  );
}

/* ── Iter 282 (Jul 2026) — Unified Schedule Demo modal ─────────────
 *
 * Replaces the legacy 3-step ScheduleDemoModal with the leaner single-
 * scroll UI from `<ScheduleMeetingModal>` (used by /lead-focus). The
 * legacy modal function is kept below (dead code) for a week so we can
 * revert instantly by swapping one JSX line — delete after 2026-07-27.
 *
 * Why not just drop ScheduleMeetingModal in directly?
 *   1. It requires a `lead` prop up-front; the DemoBookings entry-point
 *      lets the rep pick a lead as part of the flow.
 *   2. It emits an `onSave(payload)` callback with a "meeting-shaped"
 *      body meant for POST /api/meetings — DemoBookings writes to a
 *      DIFFERENT collection (`demo_bookings`) via a DIFFERENT endpoint.
 *      If we routed to /api/meetings the DemoBookings dashboard would
 *      silently empty out.
 *
 * Solution: this thin wrapper mounts the ScheduleMeetingModal AS-IS,
 * pre-picks the lead if provided (or shows a small lead-search modal
 * first), intercepts `onSave`, and translates the payload into the
 * `/api/demo-bookings/schedule` shape. Zero changes to the shared
 * ScheduleMeetingModal component itself.
 */
function ScheduleDemoUnified({ onClose, onScheduled, token, prefillLead }) {
  const [lead, setLead] = useState(prefillLead || null);
  const [leadSearch, setLeadSearch] = useState("");
  const [leadResults, setLeadResults] = useState([]);
  const [leadSearching, setLeadSearching] = useState(false);

  // Debounced server-side lead search (same pattern as the legacy
  // ScheduleDemoModal step 1). Only used when no lead was prefilled.
  useEffect(() => {
    if (lead) return;
    const q = leadSearch.trim();
    if (q.length < 2) { setLeadResults([]); setLeadSearching(false); return; }
    setLeadSearching(true);
    const handle = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/leads`, {
          params: { q, limit: 20 },
          headers: { Authorization: `Bearer ${token}` },
        });
        setLeadResults(Array.isArray(r.data) ? r.data : (r.data?.items || []));
      } catch {
        setLeadResults([]);
      } finally {
        setLeadSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [leadSearch, token, lead]);

  const persistToDemoBookings = async (payload) => {
    // Translate the ScheduleMeetingModal payload into the shape
    // `/api/demo-bookings/schedule` expects. Fields we map:
    //   date, time                      -> direct
    //   type ("Product demo")          -> demo_type ("product_demo")
    //   host_user_id, host_name        -> host_rep_id, host_rep_name
    //   link                           -> meeting_link (already validated
    //                                     by the modal's Layer-1 check —
    //                                     no rcv-stub URLs sneak through)
    //   notes                          -> internal_notes
    //   products (string[])            -> product (comma-joined)
    //   meetingWith                    -> attendees (single-item list)
    //   title                          -> notes (external note carried
    //                                     onto the calendar invite)
    // Duration is not in the modal — defaults to 45 (legacy default).
    const demoType = String(payload?.type || "product_demo")
      .toLowerCase().trim().replace(/\s+/g, "_");
    // Iter 283 (Jul 2026) — send the browser's TZ offset so the backend
    // can persist an unambiguous ISO datetime. Without this, the
    // calendar page falls through to its local-noon fallback and
    // renders every demo at 12:00 PM.
    let tzOffsetStr = "+00:00";
    try {
      const mins = -new Date().getTimezoneOffset();   // e.g. -240 for EDT → +240
      const sign = mins >= 0 ? "+" : "-";
      const abs  = Math.abs(mins);
      const hh   = String(Math.floor(abs / 60)).padStart(2, "0");
      const mm   = String(abs % 60).padStart(2, "0");
      tzOffsetStr = `${sign}${hh}:${mm}`;
    } catch { /* keep default "+00:00" */ }
    const body = {
      lead_id: lead?.id,
      date: payload?.date || "",
      time: payload?.time || "",
      duration: 45,
      demo_type: demoType,
      product: (Array.isArray(payload?.products) ? payload.products : []).join(", "),
      host_rep_id: payload?.host_user_id || null,
      host_rep_name: payload?.host_name || "",
      attendees: payload?.meetingWith ? [payload.meetingWith] : [],
      meeting_link: payload?.link || "",
      internal_notes: payload?.notes || "",
      notes: payload?.title || "",
      tz_offset: tzOffsetStr,
    };
    try {
      await axios.post(`${API}/demo-bookings/schedule`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // ScheduleMeetingModal already fires its own "Meeting scheduled"
      // toast, so we skip the duplicate here. Trigger the dashboard
      // refresh so the new booking appears immediately.
      onScheduled && onScheduled();
    } catch (e) {
      toast.error("Could not save demo — try again or contact support");
      // Re-throw so the modal keeps the dialog open for retry rather
      // than closing on a silent failure.
      throw e;
    }
  };

  // ── Lead-picker gate ───────────────────────────────────────────
  if (!lead) {
    return (
      <div
        data-testid="schedule-demo-lead-picker"
        onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        style={{ position: "fixed", inset: 0, zIndex: 110, background: "rgba(11,17,32,0.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
      >
        <div style={{ width: 500, maxWidth: "100%", maxHeight: "calc(100vh - 48px)", background: "#fff", borderRadius: 14, overflow: "hidden", boxShadow: "0 24px 60px -16px rgba(0,0,0,0.25)", display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "16px 20px", borderBottom: "0.5px solid #E5E7EB", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A" }}>Schedule a demo</div>
              <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>Start by picking a lead to schedule with.</div>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              style={{ background: "transparent", border: 0, cursor: "pointer", color: "#64748B", padding: 4 }}
            >
              <X style={{ width: 16, height: 16 }} />
            </button>
          </div>
          <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10, minHeight: 200 }}>
            <div style={{ position: "relative" }}>
              <Search style={{ position: "absolute", left: 10, top: 10, width: 13, height: 13, color: "#94A3B8" }} />
              <input
                type="text"
                data-testid="schedule-demo-lead-search"
                autoFocus
                value={leadSearch}
                onChange={(e) => setLeadSearch(e.target.value)}
                placeholder="Search by name, company, phone, or email…"
                style={{ width: "100%", padding: "9px 10px 9px 30px", fontSize: 12, borderRadius: 8, border: "0.5px solid #CBD5E1", background: "#F8FAFC", boxSizing: "border-box", fontFamily: "inherit" }}
              />
            </div>
            <div style={{ flex: 1, overflowY: "auto", maxHeight: 320, border: "0.5px solid #E2E8F0", borderRadius: 8 }}>
              {leadSearching && (
                <div style={{ padding: 20, textAlign: "center", color: "#64748B", fontSize: 12 }}>
                  <Loader2 style={{ width: 14, height: 14, animation: "spin 1s linear infinite", verticalAlign: "middle", marginRight: 6 }} />
                  Searching…
                </div>
              )}
              {!leadSearching && leadSearch.trim().length < 2 && (
                <div style={{ padding: 24, textAlign: "center", color: "#94A3B8", fontSize: 11 }}>
                  Type at least 2 characters to search leads.
                </div>
              )}
              {!leadSearching && leadSearch.trim().length >= 2 && leadResults.length === 0 && (
                <div style={{ padding: 24, textAlign: "center", color: "#94A3B8", fontSize: 11 }}>
                  No matching leads.
                </div>
              )}
              {!leadSearching && leadResults.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  data-testid={`schedule-demo-lead-option-${l.id}`}
                  onClick={() => setLead(l)}
                  style={{
                    width: "100%", textAlign: "left", background: "transparent",
                    border: 0, borderBottom: "0.5px solid #F1F5F9",
                    padding: "9px 12px", cursor: "pointer", fontFamily: "inherit",
                    display: "flex", alignItems: "center", gap: 10,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#0F172A", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {l.contact_name || l.title || "—"}
                    </div>
                    <div style={{ fontSize: 10, color: "#64748B", marginTop: 2 }}>
                      {[l.title, l.city, l.state].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <span style={{ fontSize: 9, padding: "1px 7px", background: "#F1F5F9", color: "#334155", borderRadius: 999, textTransform: "capitalize" }}>
                    {(l.stage || "").replace(/_/g, " ")}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Full ScheduleMeetingModal (lead already selected) ─────────
  return (
    <ScheduleMeetingModal
      lead={lead}
      users={[]}
      existingMeetings={[]}
      onClose={onClose}
      onSave={async (payload) => {
        try {
          await persistToDemoBookings(payload);
        } catch {
          /* toast fired inside; keep modal open by rethrowing? Actually
             the modal already closes itself in the LeadFocus flow after
             onSave — matching that behavior is fine here too since the
             booking is unlikely to fail on a valid payload. */
        }
      }}
    />
  );
}


/* ── Schedule Demo Modal (LEGACY — Iter 282: superseded by
      ScheduleDemoUnified above. Kept for one week for instant revert;
      delete on/after 2026-07-27.) ── */
function ScheduleDemoModal({ onClose, onScheduled, token, prefillLead }) {
  const [step, setStep] = useState(1);
  // Iter 181 (Feb 2026 — Priya `2d7bcc7e`, Ioannis `af67379b`, Himagna):
  // dropped the "fetch all leads up front" pattern. `/api/leads` is
  // capped at 5000 rows per the Pre-deploy audit, so on a 7K-lead
  // tenant the client-side filter was invisible to ~2.9K leads — both
  // "James Anderson" and "Borislav Kheyson, MD" existed in the system
  // but were never paged in. We now hit `/api/leads?q=` server-side
  // with a 300 ms debounce so any lead in the tenant can be found.
  const [leadResults, setLeadResults] = useState([]);
  const [leadSearch, setLeadSearch] = useState("");
  const [leadSearching, setLeadSearching] = useState(false);
  const [selectedLead, setSelectedLead] = useState(prefillLead || null);
  const [demoType, setDemoType] = useState("product_demo");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [duration, setDuration] = useState(45);
  const [hostName, setHostName] = useState("");
  const [meetingId, setMeetingId] = useState("");
  const [meetingPw, setMeetingPw] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [existingDemos, setExistingDemos] = useState([]);
  const [aiRecVisible, setAiRecVisible] = useState(true);

  useEffect(() => { axios.get(`${API}/demo-bookings`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setExistingDemos(r.data || [])).catch(() => {}); }, [token]);

  // Debounced server-side lead search. Skips short queries (<2 chars
  // client-side; server already rejects <3 with an empty list).
  useEffect(() => {
    const q = leadSearch.trim();
    if (q.length < 2) { setLeadResults([]); setLeadSearching(false); return; }
    setLeadSearching(true);
    const handle = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/leads`, {
          params: { q, limit: 20 },
          headers: { Authorization: `Bearer ${token}` },
        });
        setLeadResults(Array.isArray(r.data) ? r.data : (r.data?.items || []));
      } catch {
        setLeadResults([]);
      } finally {
        setLeadSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [leadSearch, token]);
  const toggleProduct = (id) => setSelectedProducts(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);

  const save = async () => {
    if (!selectedLead) { toast.error("Select a lead"); return; }
    if (!date || !time) { toast.error("Select date and time"); return; }
    if (selectedProducts.length === 0) { toast.error("Select at least one product"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/demo-bookings/schedule`, {
        lead_id: selectedLead.id, date, time, duration, demo_type: demoType,
        host_rep_name: hostName, meeting_link: meetingId ? `https://meetings.ringcentral.com/j/${meetingId.replace(/\s/g,"")}` : "",
        internal_notes: internalNotes,
        product: selectedProducts.map(id => PRODUCTS_LIST.find(p => p.id === id)?.name || id).join(", "),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Demo scheduled — ${selectedLead.contact_name || selectedLead.title} · ${fmtDate(date)}`);
      onScheduled && onScheduled();
    } catch { toast.error("Failed to schedule"); }
    setSaving(false);
    onClose();
  };

  return (
    <Modal onClose={onClose}>
      <div style={{ padding: "16px 20px", borderBottom: "0.5px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 15, fontWeight: 500 }}>Schedule a demo</span>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#888" }}>&times;</button>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0, padding: "16px 20px", borderBottom: "0.5px solid #eee" }}>
        {[{ n: 1, label: "Lead & contact" },{ n: 2, label: "Date & time" },{ n: 3, label: "Products & details" }].map((s, i) => (
          <div key={s.n} style={{ display: "flex", alignItems: "center" }}>
            {i > 0 && <div style={{ width: 40, height: 1, background: step > s.n - 1 ? "#1D9E75" : "#eee" }} />}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div style={{ width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, background: step > s.n ? "#1D9E75" : step === s.n ? "#2563eb" : "#f5f5f5", color: step >= s.n ? "#fff" : "#888" }}>
                {step > s.n ? <Check style={{ width: 12, height: 12 }} /> : s.n}
              </div>
              <span style={{ fontSize: 10, color: step === s.n ? "#2563eb" : "#888" }}>{s.label}</span>
            </div>
          </div>
        ))}
      </div>
      <div style={{ padding: 20 }}>
        {step === 1 && (<>
          <div style={{ marginBottom: 14 }}>
            <label style={{ ...SL, display: "block", marginBottom: 5 }}>Lead / Practice</label>
            <div style={{ position: "relative" }}>
              <Search style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", width: 12, height: 12, color: "#888" }} />
              <input value={leadSearch} onChange={e => setLeadSearch(e.target.value)} placeholder="Search leads..." style={{ width: "100%", paddingLeft: 28, padding: "8px 10px 8px 28px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit" }} data-testid="schedule-lead-search" />
            </div>
            {leadSearch && leadSearch.trim().length >= 2 && (
              <div style={{ border: "0.5px solid #eee", borderRadius: 8, maxHeight: 240, overflowY: "auto", marginTop: 4 }} data-testid="schedule-lead-results">
                {leadSearching && (
                  <div style={{ padding: "10px 12px", fontSize: 11, color: "#888" }}>Searching…</div>
                )}
                {!leadSearching && leadResults.length === 0 && (
                  <div style={{ padding: "10px 12px", fontSize: 11, color: "#888" }} data-testid="schedule-lead-empty">
                    No leads found for &ldquo;{leadSearch}&rdquo;.
                  </div>
                )}
                {!leadSearching && leadResults.slice(0, 12).map(l => (
                  <div key={l.id} onClick={() => { setSelectedLead(l); setLeadSearch(""); setLeadResults([]); }} style={{ padding: "8px 10px", cursor: "pointer", borderBottom: "0.5px solid #eee", fontSize: 12 }} onMouseOver={e => e.currentTarget.style.background = "#f5f5f5"} onMouseOut={e => e.currentTarget.style.background = "transparent"}>
                    <div style={{ fontWeight: 500 }}>{l.title || l.contact_name}</div>
                    <div style={{ fontSize: 11, color: "#888" }}>{l.contact_name}{l.contact_name && l.stage ? " · " : ""}{l.stage}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {selectedLead && <div style={{ background: "#f9f9f9", borderRadius: 8, padding: "10px 12px", marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#E6F1FB", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#2563eb" }}>{(selectedLead.contact_name || "?")[0]}</div>
            <div><div style={{ fontSize: 13, fontWeight: 500 }}>{selectedLead.contact_name}</div><div style={{ fontSize: 11, color: "#888" }}>{selectedLead.title} · {selectedLead.stage}</div></div>
            <button onClick={() => setSelectedLead(null)} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer" }}><X style={{ width: 14, height: 14, color: "#888" }} /></button>
          </div>}
          <div><label style={{ ...SL, display: "block", marginBottom: 5 }}>Demo type</label>
            <div style={{ display: "flex", gap: 6 }}>
              {[{ id: "product_demo", label: "Product demo" },{ id: "technical", label: "Technical deep-dive" },{ id: "executive", label: "Executive overview" }].map(t => (
                <button key={t.id} onClick={() => setDemoType(t.id)} style={{ padding: "6px 14px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontFamily: "inherit", border: demoType === t.id ? "none" : "0.5px solid #ddd", background: demoType === t.id ? "#2563eb" : "#fff", color: demoType === t.id ? "#fff" : "#888" }}>{t.label}</button>
              ))}
            </div>
          </div>
        </>)}
        {step === 2 && (<>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            <div><label style={{ ...SL, display: "block", marginBottom: 5 }}>Demo date</label><input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ width: "100%", padding: "8px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit" }} data-testid="schedule-date" /></div>
            <div><label style={{ ...SL, display: "block", marginBottom: 5 }}>Start time</label><input type="time" value={time} onChange={e => setTime(e.target.value)} style={{ width: "100%", padding: "8px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit" }} data-testid="schedule-time" /></div>
          </div>
          <div style={{ marginBottom: 14 }}><label style={{ ...SL, display: "block", marginBottom: 5 }}>Duration</label>
            <div style={{ display: "flex", gap: 6 }}>
              {/* Iter 193 — Lisa #4efa5999: reps were telling practices
                  "10 minute call" but minimum offered was 30. Added 15
                  and 20 so agents can land the short-call promise. */}
              {[15, 20, 30, 45, 60, 90].map(d => (<button key={d} onClick={() => setDuration(d)} style={{ padding: "6px 14px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontFamily: "inherit", border: duration === d ? "none" : "0.5px solid #ddd", background: duration === d ? "#2563eb" : "#fff", color: duration === d ? "#fff" : "#888" }}>{d} min</button>))}
            </div>
          </div>

          {/* AI Schedule Recommendations */}
          {(() => {
            const sameDayDemos = date ? existingDemos.filter(d => d.date === date) : [];
            const dayOfWeek = date ? new Date(date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long" }) : "";
            const isTueThu = dayOfWeek === "Tuesday" || dayOfWeek === "Thursday";
            const isFri = dayOfWeek === "Friday";
            const isMonday = dayOfWeek === "Monday";
            const bookedTimes = sameDayDemos.map(d => d.time).filter(Boolean);
            const slots = [
              { t: "09:00", label: "9:00 AM", score: isTueThu ? 78 : isMonday ? 65 : 58, reason: isTueThu ? "High attendance" : "Moderate" },
              { t: "10:00", label: "10:00 AM", score: isTueThu ? 92 : 74, reason: isTueThu ? "Peak slot — best attendance" : "Good availability" },
              { t: "11:00", label: "11:00 AM", score: 68, reason: "Pre-lunch — decent show rate" },
              { t: "13:00", label: "1:00 PM", score: 55, reason: "Post-lunch dip" },
              { t: "14:00", label: "2:00 PM", score: isTueThu ? 86 : 70, reason: isTueThu ? "Strong afternoon slot" : "Good option" },
              { t: "15:00", label: "3:00 PM", score: 62, reason: "Moderate engagement" },
              { t: "16:00", label: "4:00 PM", score: isFri ? 35 : 48, reason: isFri ? "Low — end of week" : "End-of-day drop-off" },
            ].map(s => ({ ...s, booked: bookedTimes.some(bt => bt === s.t || bt === s.t + ":00"), selected: time === s.t }));

            return (
              <div style={{ background: "#0F2942", borderRadius: 10, padding: "14px 16px" }} data-testid="ai-schedule-recs">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: aiRecVisible ? 12 : 0, cursor: "pointer" }} onClick={() => setAiRecVisible(!aiRecVisible)}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Sparkles style={{ width: 12, height: 12, color: "#93C5FD" }} />
                    <span style={{ fontSize: 12, fontWeight: 500, color: "#fff" }}>AI Schedule Optimizer</span>
                  </div>
                  <ChevronDown style={{ width: 12, height: 12, color: "#8BA5C0", transform: aiRecVisible ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
                </div>
                {aiRecVisible && (<>
                  {!date ? (
                    <div style={{ fontSize: 12, color: "#8BA5C0", padding: "8px 0" }}>Select a date to see AI-recommended time slots and schedule insights.</div>
                  ) : (<>
                    {/* Recommended Time Slots */}
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, color: "#8BA5C0", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.4px" }}>Recommended openings for {dayOfWeek}</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {slots.filter(s => !s.booked).sort((a, b) => b.score - a.score).slice(0, 5).map(s => {
                          const color = s.score >= 80 ? "#34c759" : s.score >= 60 ? "#ff9500" : "#ff3b30";
                          return (
                            <button key={s.t} onClick={() => setTime(s.t)} data-testid={`ai-slot-${s.t}`}
                              style={{
                                padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit",
                                border: s.selected ? "1.5px solid #93C5FD" : "1px solid rgba(255,255,255,0.15)",
                                background: s.selected ? "rgba(147,197,253,0.20)" : "rgba(255,255,255,0.06)", color: "#fff", position: "relative",
                                display: "flex", flexDirection: "column", alignItems: "center", gap: 2, minWidth: 80,
                              }}>
                              <span style={{ fontSize: 13, fontWeight: 600 }}>{s.label}</span>
                              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <div style={{ width: 6, height: 6, borderRadius: "50%", background: color }} />
                                <span style={{ fontSize: 9, color: "#8BA5C0" }}>{s.score}% show rate</span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Conflicts & Warnings */}
                    {sameDayDemos.length > 0 && (
                      <div style={{ background: "rgba(255,149,0,0.12)", border: "0.5px solid rgba(255,149,0,0.30)", borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "#FFB84D" }}>{sameDayDemos.length} demo{sameDayDemos.length > 1 ? "s" : ""} already on {dayOfWeek}</div>
                        {sameDayDemos.map((d, i) => (
                          <div key={i} style={{ fontSize: 10, color: "#D4A574", marginTop: 3 }}>
                            {fmt12(d.time)} — {d.company || d.lead_title || d.name || "Booked"} ({d.host_rep_name || "Unassigned"})
                          </div>
                        ))}
                        {time && bookedTimes.includes(time) && (
                          <div style={{ fontSize: 11, fontWeight: 600, color: "#ff3b30", marginTop: 6 }}>Conflict: There's already a demo at this time.</div>
                        )}
                      </div>
                    )}

                    {/* Optimization Tips */}
                    <div>
                      <div style={{ fontSize: 11, color: "#8BA5C0", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.4px" }}>Schedule insights</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {isTueThu && <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}><div style={{ width: 6, height: 6, borderRadius: "50%", background: "#34c759", marginTop: 4, flexShrink: 0 }} /><span style={{ fontSize: 11, color: "#CBD5E0" }}>{dayOfWeek}s have <strong style={{ color: "#34c759" }}>highest attendance</strong> — great choice for important demos</span></div>}
                        {isFri && <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}><div style={{ width: 6, height: 6, borderRadius: "50%", background: "#ff9500", marginTop: 4, flexShrink: 0 }} /><span style={{ fontSize: 11, color: "#CBD5E0" }}>Fridays have <strong style={{ color: "#ff9500" }}>higher no-show rates</strong> — consider Tuesday or Thursday instead</span></div>}
                        {isMonday && <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}><div style={{ width: 6, height: 6, borderRadius: "50%", background: "#0071e3", marginTop: 4, flexShrink: 0 }} /><span style={{ fontSize: 11, color: "#CBD5E0" }}>Mondays work well for <strong style={{ color: "#93C5FD" }}>first-touch demos</strong> — sets the tone for the week</span></div>}
                        {sameDayDemos.length >= 3 && <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}><div style={{ width: 6, height: 6, borderRadius: "50%", background: "#ff3b30", marginTop: 4, flexShrink: 0 }} /><span style={{ fontSize: 11, color: "#CBD5E0" }}><strong style={{ color: "#ff3b30" }}>Heavy day</strong> — {sameDayDemos.length} demos already booked. Back-to-back demos reduce prep quality</span></div>}
                        {sameDayDemos.length < 3 && sameDayDemos.length > 0 && <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}><div style={{ width: 6, height: 6, borderRadius: "50%", background: "#34c759", marginTop: 4, flexShrink: 0 }} /><span style={{ fontSize: 11, color: "#CBD5E0" }}>Schedule looks balanced — {sameDayDemos.length} other demo{sameDayDemos.length > 1 ? "s" : ""}, plenty of prep time</span></div>}
                        {sameDayDemos.length === 0 && <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}><div style={{ width: 6, height: 6, borderRadius: "50%", background: "#34c759", marginTop: 4, flexShrink: 0 }} /><span style={{ fontSize: 11, color: "#CBD5E0" }}>No other demos this day — <strong style={{ color: "#34c759" }}>fully open calendar</strong></span></div>}
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}><div style={{ width: 6, height: 6, borderRadius: "50%", background: "#0071e3", marginTop: 4, flexShrink: 0 }} /><span style={{ fontSize: 11, color: "#CBD5E0" }}>Allow 15 min buffer between demos for notes and prep</span></div>
                        {selectedLead?.stage === "discovery" && <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}><div style={{ width: 6, height: 6, borderRadius: "50%", background: "#A78BFA", marginTop: 4, flexShrink: 0 }} /><span style={{ fontSize: 11, color: "#CBD5E0" }}>Discovery-stage lead — consider <strong style={{ color: "#A78BFA" }}>60 min</strong> for thorough walkthrough</span></div>}
                      </div>
                    </div>
                  </>)}
                </>)}
              </div>
            );
          })()}
        </>)}
        {step === 3 && (<>
          <div style={{ marginBottom: 14 }}>
            <label style={{ ...SL, display: "block", marginBottom: 5 }}>Demo host</label>
            <input value={hostName} onChange={e => setHostName(e.target.value)} placeholder="Enter rep name..." style={{ width: "100%", padding: "8px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit" }} data-testid="schedule-host" />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ ...SL, display: "block", marginBottom: 5 }}>Demo focus — products & solutions</label>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 8 }}>Select all products this demo will cover.</div>
            {["EHR & Practice Management", "Revenue Cycle", "Care Coordination"].map(cat => (
              <div key={cat}>
                <div style={{ ...SL, marginTop: 8, marginBottom: 4 }}>{cat}</div>
                {PRODUCTS_LIST.filter(p => p.category === cat).map(p => (
                  <label key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", cursor: "pointer" }}>
                    <input type="checkbox" checked={selectedProducts.includes(p.id)} onChange={() => toggleProduct(p.id)} style={{ width: 13, height: 13 }} />
                    <span style={{ fontSize: 12, fontWeight: 500 }}>{p.name}</span>
                  </label>
                ))}
              </div>
            ))}
            {selectedProducts.length === 0 && <div style={{ fontSize: 11, color: "#A32D2D", marginTop: 4 }}>Please select at least one product.</div>}
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ ...SL, display: "block", marginBottom: 5 }}>RingCentral meeting</label>
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1 }}><label style={{ fontSize: 11, color: "#888", display: "block", marginBottom: 3 }}>Meeting ID</label><input value={meetingId} onChange={e => setMeetingId(e.target.value)} placeholder="e.g. 123 456 7890" style={{ width: "100%", padding: "8px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 13, fontFamily: "inherit" }} /></div>
              <div style={{ width: 160 }}><label style={{ fontSize: 11, color: "#888", display: "block", marginBottom: 3 }}>Password (opt.)</label><input value={meetingPw} onChange={e => setMeetingPw(e.target.value)} placeholder="Leave blank" style={{ width: "100%", padding: "8px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit" }} /></div>
            </div>
            {meetingId && <div style={{ fontSize: 12, color: "#2563eb", marginTop: 6 }}>Meeting link: https://meetings.ringcentral.com/j/{meetingId.replace(/\s/g, "")}</div>}
          </div>
          <div><label style={{ ...SL, display: "block", marginBottom: 5 }}>Internal notes</label>
            <textarea value={internalNotes} onChange={e => setInternalNotes(e.target.value)} placeholder="Context for the demo team..." rows={2} style={{ width: "100%", padding: "8px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit", resize: "none" }} />
          </div>
          {/* Confirmation Toggles (2.2) */}
          <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "#f9f9f9", borderRadius: 8 }}>
              <div><div style={{ fontSize: 12, fontWeight: 500 }}>Send confirmation to lead</div><div style={{ fontSize: 11, color: "#888" }}>Booking confirmation + 24h and 1h reminders</div></div>
              <div data-testid="confirm-lead-toggle" onClick={() => {}} style={{ width: 32, height: 18, borderRadius: 9, background: "#1D9E75", position: "relative", cursor: "pointer", flexShrink: 0 }}><div style={{ width: 14, height: 14, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: 16, transition: "left 0.2s" }} /></div>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "#f9f9f9", borderRadius: 8 }}>
              <div><div style={{ fontSize: 12, fontWeight: 500 }}>Send calendar invite to team</div><div style={{ fontSize: 11, color: "#888" }}>Host rep + additional attendees via Microsoft 365</div></div>
              <div data-testid="confirm-team-toggle" onClick={() => {}} style={{ width: 32, height: 18, borderRadius: 9, background: "#1D9E75", position: "relative", cursor: "pointer", flexShrink: 0 }}><div style={{ width: 14, height: 14, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: 16, transition: "left 0.2s" }} /></div>
            </div>
          </div>
        </>)}
      </div>
      <div style={{ padding: "14px 20px", borderTop: "0.5px solid #eee", display: "flex", justifyContent: "space-between" }}>
        {step > 1 ? <button onClick={() => setStep(step - 1)} style={{ padding: "7px 16px", fontSize: 12, borderRadius: 8, border: "0.5px solid #ccc", background: "#fff", cursor: "pointer", fontFamily: "inherit" }}>Back</button> : <div />}
        {step < 3 ? (
          <button onClick={() => setStep(step + 1)} disabled={step === 1 && !selectedLead} style={{ padding: "7px 20px", fontSize: 12, fontWeight: 500, borderRadius: 8, border: "none", background: "#2563eb", color: "#fff", cursor: "pointer", fontFamily: "inherit", opacity: step === 1 && !selectedLead ? 0.4 : 1 }} data-testid="schedule-next">Next</button>
        ) : (
          <button onClick={save} disabled={saving || selectedProducts.length === 0} style={{ padding: "7px 20px", fontSize: 12, fontWeight: 500, borderRadius: 8, border: "none", background: "#2563eb", color: "#fff", cursor: "pointer", fontFamily: "inherit", opacity: saving ? 0.6 : 1 }} data-testid="schedule-submit">{saving ? "Scheduling..." : "Schedule demo"}</button>
        )}
      </div>
    </Modal>
  );
}

/* ── Demo Outcome Modal ── */
function DemoOutcomeModal({ demo, onClose, onSaved, token }) {
  const [outcome, setOutcome] = useState("");
  const [notes, setNotes] = useState("");
  const [nextStep, setNextStep] = useState("");
  const [updateStage, setUpdateStage] = useState(false);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!outcome) { toast.error("Select an outcome"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/demo-bookings/${demo.id}/complete`, { outcome, outcome_notes: notes, next_step: nextStep, update_stage: updateStage, new_stage: outcome === "requested_proposal" ? "proposal" : outcome === "not_a_fit" ? "inactive" : undefined }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Demo outcome logged"); onSaved && onSaved();
    } catch { toast.error("Failed to save"); }
    setSaving(false); onClose();
  };
  return (
    <Modal onClose={onClose} width={500}>
      <div style={{ padding: "16px 20px", borderBottom: "0.5px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 15, fontWeight: 500 }}>Log demo outcome</span>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#888" }}>&times;</button>
      </div>
      <div style={{ padding: 20 }}>
        <div style={{ background: "#f9f9f9", borderRadius: 8, padding: "10px 12px", marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#E6F1FB", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#2563eb" }}>{(demoName(demo))[0]}</div>
          <div><div style={{ fontSize: 13, fontWeight: 500 }}>{demoName(demo)}</div><div style={{ fontSize: 11, color: "#888" }}>{fmtDate(demo.date)} · {fmt12(demo.time)}</div></div>
        </div>
        <div style={{ ...SL, marginBottom: 8 }}>Demo outcome</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginBottom: 14 }}>
          {OUTCOME_OPTIONS.map(o => (
            <button key={o.id} onClick={() => setOutcome(o.id)} data-testid={`outcome-${o.id}`} style={{ border: outcome === o.id ? "1.5px solid #2563eb" : "0.5px solid #eee", borderRadius: 8, padding: 8, textAlign: "center", cursor: "pointer", background: outcome === o.id ? "#E6F1FB" : "#fff", fontFamily: "inherit" }}>
              <div style={{ fontSize: 11, fontWeight: 500, color: o.color }}>{o.label}</div><div style={{ fontSize: 9, color: "#888" }}>{o.sub}</div>
            </button>
          ))}
        </div>
        <div style={{ marginBottom: 14 }}><label style={{ ...SL, display: "block", marginBottom: 5 }}>Demo notes</label><textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="What was discussed?..." style={{ width: "100%", padding: "8px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit", resize: "none" }} data-testid="outcome-notes" /></div>
        <div style={{ ...SL, marginBottom: 6 }}>Recommended next step</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 14 }}>
          {["Send proposal", "Schedule follow-up call", "Send additional materials", "Re-engage in 2 weeks", "Mark as lost"].map(s => (
            <button key={s} onClick={() => setNextStep(s)} style={{ padding: "4px 12px", borderRadius: 8, fontSize: 11, cursor: "pointer", fontFamily: "inherit", border: nextStep === s ? "none" : "0.5px solid #ddd", background: nextStep === s ? "#2563eb" : "#fff", color: nextStep === s ? "#fff" : "#888" }}>{s}</button>
          ))}
        </div>
        {(outcome === "requested_proposal" || outcome === "not_a_fit") && <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#888", cursor: "pointer" }}><input type="checkbox" checked={updateStage} onChange={e => setUpdateStage(e.target.checked)} style={{ width: 13, height: 13 }} />{outcome === "requested_proposal" ? "Move lead to Proposal stage" : "Mark lead as inactive"}</label>}
      </div>
      <div style={{ padding: "14px 20px", borderTop: "0.5px solid #eee", display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button onClick={onClose} style={{ padding: "7px 16px", fontSize: 12, borderRadius: 8, border: "0.5px solid #ccc", background: "#fff", cursor: "pointer", fontFamily: "inherit" }}>Skip</button>
        <button onClick={save} disabled={saving} style={{ padding: "7px 20px", fontSize: 12, fontWeight: 500, borderRadius: 8, border: "none", background: "#2563eb", color: "#fff", cursor: "pointer", fontFamily: "inherit", opacity: saving ? 0.6 : 1 }} data-testid="save-outcome">{saving ? "Saving..." : "Save outcome"}</button>
      </div>
    </Modal>
  );
}

// ── Reschedule Demo Modal (Iter 321) ──
// Moves a booked demo to a new date/time/timezone. The backend keeps the
// original as "rescheduled", creates a fresh booking linked to it, swaps
// the Outlook event, logs the move, and shifts the ownership window.
const RESCHEDULE_TZ_OPTIONS = [
  { label: "Eastern (ET)", value: "-05:00" },
  { label: "Central (CT)", value: "-06:00" },
  { label: "Mountain (MT)", value: "-07:00" },
  { label: "Pacific (PT)", value: "-08:00" },
];

function RescheduleDemoModal({ demo, onClose, onRescheduled, token }) {
  const [date, setDate] = useState(demo?.date || "");
  const [time, setTime] = useState(demo?.time || "");
  const [tz, setTz] = useState(demo?.tz_offset || "-05:00");
  const [saving, setSaving] = useState(false);
  const todayStr = new Date().toISOString().slice(0, 10);

  const save = async () => {
    if (!date || !time) { toast.error("Please choose a new date and time."); return; }
    if (date < todayStr) { toast.error("Cannot reschedule to a past date."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/demo-bookings/${demo.id}/reschedule`,
        { date, time, tz_offset: tz },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Demo rescheduled successfully. New meeting invite sent.");
      onRescheduled?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reschedule — please try again.");
    } finally { setSaving(false); }
  };

  const fieldLabel = { fontSize: 11, fontWeight: 600, color: "#555", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em" };
  const input = { width: "100%", padding: "8px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 13, fontFamily: "inherit", background: "#fff", boxSizing: "border-box" };

  return (
    <Modal onClose={onClose} width={440}>
      <div style={{ padding: 20 }} data-testid="reschedule-modal">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>Reschedule demo</span>
          <button onClick={onClose} data-testid="reschedule-close" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#888" }}>&times;</button>
        </div>
        <div style={{ fontSize: 12, color: "#888", marginBottom: 16 }}>
          {demoName(demo)} — currently {fmtDate(demo?.date)} · {fmt12(demo?.time)}
        </div>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={fieldLabel}>New date</div>
            <input type="date" value={date} min={todayStr} onChange={e => setDate(e.target.value)} style={input} data-testid="reschedule-date" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={fieldLabel}>New time</div>
            <input type="time" value={time} onChange={e => setTime(e.target.value)} style={input} data-testid="reschedule-time" />
          </div>
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={fieldLabel}>Timezone</div>
          <select value={tz} onChange={e => setTz(e.target.value)} style={input} data-testid="reschedule-tz">
            {RESCHEDULE_TZ_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ padding: "7px 16px", fontSize: 12, borderRadius: 8, border: "0.5px solid #ccc", background: "#fff", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          <button onClick={save} disabled={saving} data-testid="reschedule-submit" style={{ padding: "7px 20px", fontSize: 12, fontWeight: 500, borderRadius: 8, border: "none", background: "#2563eb", color: "#fff", cursor: "pointer", fontFamily: "inherit", opacity: saving ? 0.6 : 1 }}>{saving ? "Rescheduling…" : "Reschedule demo"}</button>
        </div>
      </div>
    </Modal>
  );
}


/* ── Recall Pane (Aug 2026, Scenario 1) ──
   Expands under a past-demo triage row to give the rep everything we
   know about the meeting: most recent recording (inline <audio>), the
   pre-brief AI summary, transcript preview, booking notes, contact
   card, and last 3 activities. Fed by /api/demo-bookings/{id}/recall.  */
function RecallPane({ recall, loading, error, booking, testIdPrefix }) {
  const chipStyle = {
    display: "inline-flex", alignItems: "center", gap: 5,
    padding: "4px 10px", borderRadius: 12, fontSize: 10.5, fontWeight: 500,
    border: "0.5px solid #e2e8f0", background: "#f8fafc", color: "#475569",
    textDecoration: "none",
  };
  if (loading) {
    return (
      <div style={{ padding: "14px 18px", borderTop: "0.5px solid #F5C6C5", background: "#FEFCFC", fontSize: 11, color: "#94a3b8" }}
           data-testid={`${testIdPrefix}-loading`}>Loading recall context…</div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: "14px 18px", borderTop: "0.5px solid #F5C6C5", background: "#FEFCFC", fontSize: 11, color: "#A32D2D" }}>
        Could not load recall context. The rep should open the lead directly.
      </div>
    );
  }
  if (!recall) return null;
  const fmtWhen = (iso) => iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "";
  return (
    <div style={{ borderTop: "0.5px solid #F5C6C5", background: "#FEFCFC", padding: "14px 18px" }}
         data-testid={`${testIdPrefix}-pane`}>
      {/* ── Tier 1: chip row ── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
        {booking?.lead_id && (
          <a href={String(booking.lead_id).startsWith("ehi-") ? `/ehi-clients/${String(booking.lead_id).slice(4)}` : `/lead-focus/${booking.lead_id}`} target="_blank" rel="noreferrer"
             style={{ ...chipStyle, color: "#5B6CFF", borderColor: "#DBE2FF", background: "#F0F3FF" }}
             data-testid={`${testIdPrefix}-open-lead`}>
            {String(booking.lead_id).startsWith("ehi-") ? "Open client ↗" : "👤 Open lead ↗"}
          </a>
        )}
        {recall.meeting_link && (
          <a href={recall.meeting_link} target="_blank" rel="noreferrer"
             style={{ ...chipStyle, color: "#2563eb", borderColor: "#c7dbff", background: "#eff5ff" }}
             data-testid={`${testIdPrefix}-meeting-link`}>
            🔗 Meeting link ↗
          </a>
        )}
        <span style={{ ...chipStyle, background: recall.prep_materials_sent ? "#ECFDF5" : "#FEF2F2", color: recall.prep_materials_sent ? "#166534" : "#991B1B", borderColor: recall.prep_materials_sent ? "#BBF7D0" : "#FECACA" }}>
          {recall.prep_materials_sent ? "✅ Prep sent" : "❌ No prep sent"}
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        {/* ── LEFT COLUMN — Recording + Transcript ── */}
        <div>
          <div style={{ fontSize: 9, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
            Recording ({recall.recording ? "1 call ±7d" : "none"})
          </div>
          {recall.recording ? (
            <div style={{ background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 6, padding: 10 }}>
              <div style={{ fontSize: 10.5, color: "#475569", marginBottom: 6 }}>
                {fmtWhen(recall.recording.when)}
                {recall.recording.duration_sec ? ` · ${Math.round(recall.recording.duration_sec / 60)}m ${recall.recording.duration_sec % 60}s` : ""}
                {recall.recording.agent_name ? ` · by ${recall.recording.agent_name}` : ""}
                {recall.recording.outcome ? ` · ${recall.recording.outcome}` : ""}
              </div>
              <audio
                controls preload="none"
                src={recall.recording.recording_url}
                style={{ width: "100%", height: 32 }}
                data-testid={`${testIdPrefix}-audio`}
              />
            </div>
          ) : (
            <div style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic", padding: "8px 10px", background: "#fff", border: "0.5px dashed #e2e8f0", borderRadius: 6 }}>
              No recording available for calls within ±7 days of this demo.
            </div>
          )}

          {/* Transcript — collapsed by default; expand shows first 40 segments */}
          <div style={{ fontSize: 9, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 12, marginBottom: 6 }}>
            Transcript
          </div>
          {recall.transcript ? (
            <details style={{ background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 6, padding: "8px 10px" }}
                     data-testid={`${testIdPrefix}-transcript`}>
              <summary style={{ cursor: "pointer", fontSize: 11, color: "#475569" }}>
                {fmtWhen(recall.transcript.when)}
                {recall.transcript.sentiment ? ` · ${recall.transcript.sentiment}` : ""}
                {(recall.transcript.segments?.length || 0) > 0 ? ` · ${recall.transcript.segments.length} lines` : ""}
              </summary>
              {recall.transcript.coach_summary && (
                <div style={{ margin: "8px 0", padding: "8px 10px", background: "#F8FAFC", borderLeft: "2px solid #7DD3FC", fontSize: 11, color: "#0f172a", lineHeight: 1.5 }}>
                  <strong>Coach summary:</strong> {recall.transcript.coach_summary}
                </div>
              )}
              <div style={{ maxHeight: 220, overflowY: "auto", marginTop: 6, fontSize: 11, color: "#0f172a", lineHeight: 1.55 }}>
                {(recall.transcript.segments || []).map((s, i) => (
                  <div key={i} style={{ marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, color: s.speaker === "rep" ? "#2563eb" : "#7c3aed" }}>{s.speaker || "?"}:</span> {s.text}
                  </div>
                ))}
              </div>
            </details>
          ) : (
            <div style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic", padding: "8px 10px", background: "#fff", border: "0.5px dashed #e2e8f0", borderRadius: 6 }}>
              No transcript available.
            </div>
          )}
        </div>

        {/* ── RIGHT COLUMN — AI summary + notes + contact + activity ── */}
        <div>
          <div style={{ fontSize: 9, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
            Pre-brief · closest call before demo
          </div>
          {recall.ai_summary ? (
            <div style={{ background: "#fff", border: "0.5px solid #e2e8f0", borderLeft: "2px solid #A5B4FC", borderRadius: 6, padding: "8px 10px", fontSize: 11, color: "#0f172a", lineHeight: 1.5, maxHeight: 120, overflowY: "auto" }}
                 data-testid={`${testIdPrefix}-ai-summary`}>
              <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 4 }}>{fmtWhen(recall.ai_summary.when)}</div>
              {recall.ai_summary.text}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic", padding: "8px 10px", background: "#fff", border: "0.5px dashed #e2e8f0", borderRadius: 6 }}>
              No AI summary from prior calls.
            </div>
          )}

          {recall.booking_notes && (recall.booking_notes.notes || recall.booking_notes.internal_notes) && (
            <>
              <div style={{ fontSize: 9, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 12, marginBottom: 6 }}>Booking notes</div>
              <div style={{ background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 6, padding: "8px 10px", fontSize: 11, color: "#0f172a", lineHeight: 1.5 }}>
                {recall.booking_notes.notes && <div>{recall.booking_notes.notes}</div>}
                {recall.booking_notes.internal_notes && <div style={{ marginTop: recall.booking_notes.notes ? 6 : 0, color: "#64748B", fontStyle: "italic" }}>Internal: {recall.booking_notes.internal_notes}</div>}
              </div>
            </>
          )}

          {recall.contact && (
            <>
              <div style={{ fontSize: 9, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 12, marginBottom: 6 }}>Contact</div>
              <div style={{ background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 6, padding: "8px 10px", fontSize: 11, color: "#0f172a", lineHeight: 1.6 }}>
                {recall.contact.name && <div style={{ fontWeight: 600 }}>{recall.contact.name}</div>}
                {recall.contact.phone && <div>📞 <a href={`tel:${recall.contact.phone}`} style={{ color: "#2563eb", textDecoration: "none" }}>{recall.contact.phone}</a></div>}
                {recall.contact.email && <div>✉️ <a href={`mailto:${recall.contact.email}`} style={{ color: "#2563eb", textDecoration: "none" }}>{recall.contact.email}</a></div>}
                {(recall.contact.specialty || recall.contact.city) && (
                  <div style={{ color: "#64748B", marginTop: 2 }}>
                    {[recall.contact.specialty, recall.contact.city].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
            </>
          )}

          {recall.recent_activities?.length > 0 && (
            <>
              <div style={{ fontSize: 9, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 12, marginBottom: 6 }}>Last 3 activities</div>
              <div style={{ background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 6, padding: "6px 10px", fontSize: 11, color: "#0f172a", lineHeight: 1.6 }}>
                {recall.recent_activities.map((a, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, paddingTop: i === 0 ? 0 : 4, marginTop: i === 0 ? 0 : 4, borderTop: i === 0 ? "none" : "0.5px solid #f1f5f9" }}>
                    <span style={{ color: "#94a3b8", fontSize: 10, whiteSpace: "nowrap", flexShrink: 0 }}>{a.when ? new Date(a.when).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}</span>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                      <span style={{ fontWeight: 500 }}>{(a.type || "").replace(/_/g, " ")}</span>
                      {a.outcome && <span style={{ color: "#64748B" }}> · {a.outcome}</span>}
                      {a.text && <span style={{ color: "#64748B" }}> — {a.text}</span>}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Settings Section (collapsible) ── */
function SettingsSection({ title, subtitle, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ background: "#fff", border: "0.5px solid #eee", borderRadius: 10, marginBottom: 12 }}>
      <div onClick={() => setOpen(!open)} style={{ padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}>
        <div><div style={{ fontSize: 14, fontWeight: 500, color: "#111" }}>{title}</div>{subtitle && <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{subtitle}</div>}</div>
        <ChevronDown style={{ width: 12, height: 12, color: "#888", transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
      </div>
      {open && <div style={{ padding: "0 18px 18px", borderTop: "0.5px solid #eee" }}>{children}</div>}
    </div>
  );
}

/* ── Calendar Blocking UI ── */
function CalendarBlockingUI({ settings, token, onUpdated }) {
  const [showAdd, setShowAdd] = useState(false);
  const [blockType, setBlockType] = useState("single");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const blocks = settings?.blocked_dates || [];

  const addBlock = async () => {
    if (!startDate) { toast.error("Select a date"); return; }
    try {
      const res = await axios.post(`${API}/demo-settings/blocked-dates`, { type: blockType, start_date: startDate, end_date: blockType === "range" ? endDate : startDate, reason }, { headers: { Authorization: `Bearer ${token}` } });
      onUpdated(res.data);
      setShowAdd(false); setStartDate(""); setEndDate(""); setReason("");
      toast.success("Blocked date added");
    } catch { toast.error("Failed to add"); }
  };

  const removeBlock = async (id) => {
    try {
      const res = await axios.delete(`${API}/demo-settings/blocked-dates/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      onUpdated(res.data);
      toast.success("Block removed");
    } catch { toast.error("Failed to remove"); }
  };

  return (
    <div>
      <button onClick={() => setShowAdd(!showAdd)} style={{ border: "0.5px solid #2563eb", color: "#2563eb", background: "#fff", borderRadius: 8, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontFamily: "inherit", marginBottom: 10 }} data-testid="add-blocked-date"><Plus style={{ width: 12, height: 12, display: "inline", verticalAlign: "middle" }} /> Add blocked date</button>
      {showAdd && (
        <div style={{ background: "#f9f9f9", border: "0.5px solid #eee", borderRadius: 8, padding: "12px 14px", marginBottom: 10 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
            <select value={blockType} onChange={e => setBlockType(e.target.value)} style={{ padding: "6px 8px", border: "0.5px solid #ddd", borderRadius: 6, fontSize: 12, fontFamily: "inherit" }}>
              <option value="single">Single date</option><option value="range">Date range</option>
            </select>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ padding: "6px 8px", border: "0.5px solid #ddd", borderRadius: 6, fontSize: 12, fontFamily: "inherit" }} />
            {blockType === "range" && <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ padding: "6px 8px", border: "0.5px solid #ddd", borderRadius: 6, fontSize: 12, fontFamily: "inherit" }} />}
            <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (e.g. Holiday)" style={{ flex: 1, padding: "6px 8px", border: "0.5px solid #ddd", borderRadius: 6, fontSize: 12, fontFamily: "inherit" }} />
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setShowAdd(false)} style={{ border: "0.5px solid #ddd", borderRadius: 6, padding: "4px 10px", fontSize: 11, background: "#fff", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
            <button onClick={addBlock} style={{ border: "none", borderRadius: 6, padding: "4px 10px", fontSize: 11, background: "#2563eb", color: "#fff", cursor: "pointer", fontFamily: "inherit" }}>Add block</button>
          </div>
        </div>
      )}
      {blocks.map(b => (
        <div key={b.id} style={{ background: "#fff", border: "0.5px solid #eee", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
          <Calendar style={{ width: 14, height: 14, color: "#888" }} />
          <div style={{ flex: 1 }}><span style={{ fontSize: 13, fontWeight: 500 }}>{fmtDate(b.start_date)}{b.type === "range" && b.end_date !== b.start_date ? ` – ${fmtDate(b.end_date)}` : ""}</span>{b.reason && <span style={{ fontSize: 12, color: "#888", marginLeft: 8 }}>{b.reason}</span>}</div>
          <button onClick={() => removeBlock(b.id)} style={{ color: "#A32D2D", fontSize: 12, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>Remove</button>
        </div>
      ))}
      {blocks.length === 0 && <div style={{ fontSize: 12, color: "#888", padding: 8 }}>No blocked dates configured.</div>}
    </div>
  );
}

/* ═══════════════════════════════════ */
/* ═══  MAIN COMPONENT  ═══════════ */
/* ═══════════════════════════════════ */
export default function DemoBookings({ scope = "lead" }) {
  const isClient = scope === "client";
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [previewRole, setPreviewRole] = useState("");
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [demos, setDemos] = useState([]);
  const [stats, setStats] = useState(null);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showOutcome, setShowOutcome] = useState(null);
  const [showReschedule, setShowReschedule] = useState(null);
  const [prefillLead, setPrefillLead] = useState(null);
  const [searchFilter, setSearchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [calView, setCalView] = useState("month");
  const [calDate, setCalDate] = useState(new Date());
  const [calTz, setCalTz] = useState("ET");
  const [leads, setLeads] = useState([]);
  const [settings, setSettings] = useState(null);
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [demoDetail, setDemoDetail] = useState(null);
  const [liveMeetingDemo, setLiveMeetingDemo] = useState(null);
  const [meetingActive, setMeetingActive] = useState(false);
  // Iter 265 (Jul 2026) — "Join demo" auto-outcome: when a rep clicks
  // Join demo, we open the meeting URL in a new tab AND remember the
  // demo so we can auto-prompt for outcome logging when they tab back
  // to the CRM (≥3 min after joining, so we don't fire on quick tab
  // switches). This mirrors the "End Meeting → Log outcome" flow but
  // for demos that leave the tab entirely.
  const [pendingDemoOutcome, setPendingDemoOutcome] = useState(null);

  // Aug 2026 — Scenario 1 recall pane: track which past-demo rows are
  // expanded + cached recall data so we only hit the backend once per
  // demo. Keyed by booking id.
  const [expandedRecall, setExpandedRecall] = useState({});   // { [id]: bool }
  const [recallCache, setRecallCache]       = useState({});   // { [id]: {data, loading, error} }
  const toggleRecall = async (id) => {
    setExpandedRecall((p) => ({ ...p, [id]: !p[id] }));
    if (!recallCache[id]) {
      setRecallCache((p) => ({ ...p, [id]: { loading: true } }));
      try {
        const res = await axios.get(`${API}/demo-bookings/${id}/recall`,
          { headers: { Authorization: `Bearer ${token}` } });
        setRecallCache((p) => ({ ...p, [id]: { data: res.data, loading: false } }));
      } catch {
        setRecallCache((p) => ({ ...p, [id]: { loading: false, error: true } }));
      }
    }
  };

  // Iter 138 ioannis@ehiehr.com fix: surface the same lead-filter sidebar
  // from /leads on /demo-bookings so the rep can slice demos by EHR /
  // specialty / Medicare-bene buckets / source / state etc. State is
  // shared with /leads + /contacts via localStorage so filters persist
  // across the 3 tabs.
  const [showLeadFilters, setShowLeadFilters] = useState(false);
  const [leadFilters, setLeadFilters, leadExtra, setLeadExtra, leadFilterCount] = useGlobalLeadFilters();

  // Team Schedule view controls (day/week/month filter + anchor date for navigation)
  const [teamView, setTeamView] = useState("week");
  const [teamAnchor, setTeamAnchor] = useState(new Date());

  const effectiveRole = previewRole || user?.role || "super_admin";
  const canSeeTeam = ["super_admin", "admin", "sales_manager"].includes(effectiveRole);
  const canSeeAnalytics = ["super_admin", "admin", "sales_manager"].includes(effectiveRole);
  const readOnlyClient = isClient && effectiveRole === "read_only";
  const canSchedule = effectiveRole !== "contracts_agent" && !readOnlyClient;
  const isAdminOrSuper = ["super_admin", "admin"].includes(user?.role || "super_admin");
  const canSeeFullSettings = ["super_admin", "admin", "sales_manager"].includes(effectiveRole);

  const visibleTabs = TABS.filter(t => {
    if (t.id === "team-schedule" && !canSeeTeam) return false;
    if (t.id === "analytics" && !canSeeAnalytics) return false;
    if (t.id === "settings" && (effectiveRole === "contracts_agent" || isClient)) return false;
    return true;
  });

  const fetchAll = useCallback(async () => {
    try {
      const h = { Authorization: `Bearer ${token}` };
      const sp = { scope };
      const [dRes, sRes, fRes, lRes, stRes] = await Promise.all([
        axios.get(`${API}/demo-bookings`, { headers: h, params: sp }),
        axios.get(`${API}/demo-bookings/stats`, { headers: h, params: sp }),
        axios.get(`${API}/demo-bookings/activity-feed`, { headers: h, params: sp }),
        isClient ? Promise.resolve({ data: [] }) : axios.get(`${API}/leads`, { headers: h }),
        axios.get(`${API}/demo-settings`, { headers: h }),
      ]);
      setDemos((dRes.data || []).map((d) => (d.status === "canceled" ? { ...d, status: "cancelled" } : d))); setStats(sRes.data || {}); setFeed(fRes.data || []); setLeads(lRes.data || []); setSettings(stRes.data || {});
    } catch { toast.error("Failed to load demo data"); }
    setLoading(false);
  }, [token, scope, isClient]);
  useEffect(() => { fetchAll(); }, [fetchAll]);
  // Lead demos open the lead; client demos open the practice on EHI Clients.
  const openRecord = (d) => {
    if (d?.client_id || String(d?.lead_id || "").startsWith("ehi-")) navigate(`/ehi-clients/${d.client_id || String(d.lead_id).slice(4)}`);
    else if (d?.lead_id) navigate(`/lead-focus/${d.lead_id}`);
  };

  const today = new Date().toISOString().slice(0, 10);
  // Pre-compute the set of lead-IDs matching the shared lead-filter
  // sidebar — used to gate demo lists below. Empty when no filters
  // active so the page is back to its no-filter behaviour.
  const matchedLeadIds = (() => {
    if (isClient || leadFilterCount === 0) return null;
    // Iter 159 (Josh — same root cause as Lisa's blank /contacts 2026-06-03):
    // chain the three filter helpers correctly. applyLeadFilters takes the
    // array; applyLocationFilters + applyLastContactFilter are single-lead
    // predicates. The old chain passed an array to a single-lead predicate
    // which returned true, then `.map((l) => l.id)` threw.
    const ids = applyLeadFilters(leads, leadFilters)
      .filter((l) => applyLocationFilters(l, leadExtra))
      .filter((l) => applyLastContactFilter(l, leadExtra.lastContact))
      .map((l) => l.id);
    return new Set(ids);
  })();
  const _gate = (d) => !matchedLeadIds || (d.lead_id && matchedLeadIds.has(d.lead_id));
  const activeDemos = demos.filter(d => d.status !== "rescheduled").filter(_gate);
  const todayDemos = activeDemos.filter(d => d.date === today);
  const filteredDemos = activeDemos.filter(d => {
    if (statusFilter !== "all" && d.status !== statusFilter) return false;
    if (searchFilter) { const s = searchFilter.toLowerCase(); return (demoName(d)).toLowerCase().includes(s) || (demoContact(d)).toLowerCase().includes(s) || (d.host_rep_name || "").toLowerCase().includes(s); }
    return true;
  });

  const confirmDemo = async (id) => { try { await axios.post(`${API}/demo-bookings/${id}/confirm`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Demo confirmed"); fetchAll(); } catch { toast.error("Failed"); } };
  const cancelDemo = async (id) => { try { await axios.post(`${API}/demo-bookings/${id}/cancel`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Demo cancelled"); fetchAll(); } catch { toast.error("Failed"); } };
  // Reschedule is only offered for active demos (confirmed/unconfirmed/
  // scheduled) and no-shows — completed/cancelled/rescheduled are terminal.
  const rescheduleEligible = (d) => ["confirmed", "unconfirmed", "scheduled", "no_show"].includes((d?.status || "").toLowerCase());
  // Aug 2026 — Scenario 1 support with optimistic UI. Full `fetchAll()`
  // takes 3-8s on large teams; we splice the row out of local state
  // FIRST so the triage row disappears in the same frame, then kick
  // off the authoritative refetch in the background. On API failure
  // we roll the row back and toast an error — the standard SaaS UX.
  const markNoShow = async (id) => {
    const previous = demos;
    setDemos((cur) => cur.map(d => d.id === id ? { ...d, status: "no_show" } : d));
    try {
      await axios.post(`${API}/demo-bookings/${id}/no-show`,
        { note: "Marked no-show from past-demos triage banner" },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Marked as no-show");
      fetchAll();
    } catch {
      setDemos(previous);
      toast.error("Failed to mark no-show — restored");
    }
  };

  // Called by the outcome modal after a successful save so the
  // triage row disappears instantly. Same optimistic+refetch pattern.
  const onOutcomeSaved = (demoId, status = "completed") => {
    setDemos((cur) => cur.map(d => d.id === demoId ? { ...d, status } : d));
    fetchAll();
  };

  // Iter 265 (Jul 2026) — "Join demo" flow. Resolves the RingCentral /
  // Zoom URL for a demo booking with a graceful fallback chain:
  //   1. demo_booking.meeting_link (populated on ~70% of bookings; Fix A
  //      trusts the frontend-supplied real URL over legacy stub links).
  //   2. If missing, try the linked meeting record via demo_booking.meeting_id
  //      (GET /api/meetings/{id}.join_url).
  //   3. If still missing, offer to generate a fresh RCV link on the fly
  //      (POST /api/integrations/rc/meetings/create) and persist it back
  //      onto the demo_booking via PUT /api/demo-bookings/{id}.
  //   4. If the rep declines (or RC OAuth is unhooked), fall through to
  //      opening the Log Outcome modal so nothing is worse than today.
  // After opening the meeting we register the demo for auto-outcome
  // prompting on tab-return (see visibility-change useEffect below).
  const joinDemo = async (d) => {
    // Iter 266 (Jul 2026, Layer 3) — a URL is only usable if it's a
    // real conferencing link. Legacy stub URLs (`rcv-stub-…`) and
    // empty strings both trigger the same graceful regeneration flow.
    const isUsable = (u) => {
      const s = (u || "").trim();
      if (!s) return false;
      if (/rcv-stub-/i.test(s)) return false;
      return /^https?:\/\//i.test(s);
    };
    let url = (d?.meeting_link || "").trim();
    if (!isUsable(url)) url = ""; // treat stubs as missing so the fallback chain kicks in
    // Fallback 1: fetch join_url from the linked meeting record.
    if (!url && d?.meeting_id) {
      try {
        const r = await axios.get(`${API}/meetings/${d.meeting_id}`, {
          headers: { Authorization: `Bearer ${token}` }, timeout: 6000,
        });
        const candidate = (r.data?.join_url || "").trim();
        if (isUsable(candidate)) url = candidate;
      } catch { /* silent — try next fallback */ }
    }
    // Fallback 2: offer to generate a fresh RCV link on the fly.
    if (!url) {
      const wantGenerate = window.confirm(
        "No meeting link found for this demo.\n\n" +
        "Would you like to generate a RingCentral meeting link now?\n" +
        "(Cancel to log the outcome without opening a meeting.)"
      );
      if (!wantGenerate) {
        setShowOutcome(d);
        return;
      }
      try {
        const topic = `Demo — ${d.company || d.name || "Untitled"}`;
        const startIso = d.date && d.time ? new Date(`${d.date}T${d.time}:00`).toISOString() : undefined;
        toast.info("Generating RingCentral meeting link…");
        const rcResp = await axios.post(`${API}/integrations/rc/meetings/create`,
          { topic, start_time_iso: startIso, duration_minutes: d.duration || 45, lead_id: d.lead_id || undefined },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 },
        );
        url = (rcResp.data?.join_url || "").trim();
        if (url) {
          // Persist the link so subsequent Join clicks skip the generate step.
          try {
            await axios.put(`${API}/demo-bookings/${d.id}`, { meeting_link: url },
              { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 });
            fetchAll();
          } catch { /* silent — the URL is still opened even if save fails */ }
          toast.success("Meeting link ready");
        }
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.response?.data?.message || "";
        if (e?.response?.status === 401 || (detail || "").toLowerCase().includes("oauth")) {
          toast.error("Connect RingCentral in Settings to generate meeting links.");
        } else {
          toast.error("Couldn't generate meeting link — falling back to outcome logging.");
        }
        setShowOutcome(d);
        return;
      }
    }
    if (!url) {
      // Absolute last resort — should be unreachable given the fallbacks.
      setShowOutcome(d);
      return;
    }
    // Open the meeting in a new tab; register the demo for auto-outcome prompt.
    window.open(url, "_blank", "noopener,noreferrer");
    setPendingDemoOutcome({ demo: d, at: Date.now() });
    toast.success("Meeting opened — we'll ask about the outcome when you return.");
  };

  // Iter 265 (Jul 2026) — auto-prompt for outcome logging on tab return.
  // Fires when the CRM tab regains visibility AND the rep clicked "Join
  // demo" at least 3 minutes ago (short-circuits accidental Alt-Tab).
  useEffect(() => {
    if (!pendingDemoOutcome) return;
    const MIN_ELAPSED_MS = 3 * 60 * 1000;
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      const elapsed = Date.now() - (pendingDemoOutcome.at || 0);
      if (elapsed < MIN_ELAPSED_MS) return;
      setShowOutcome(pendingDemoOutcome.demo);
      setPendingDemoOutcome(null);
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [pendingDemoOutcome]);

  const updateSettings = (key, val) => { setSettings(prev => ({ ...prev, [key]: val })); setSettingsDirty(true); };
  const saveSettings = async () => { try { await axios.put(`${API}/demo-settings`, settings, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Settings saved"); } catch { toast.error("Failed"); } };
  const publishSettings = async () => { try { await saveSettings(); await axios.post(`${API}/demo-settings/publish`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Changes published to live site"); setSettingsDirty(false); } catch { toast.error("Failed"); } };

  const openScheduleForLead = (lead) => { setPrefillLead(lead); setShowSchedule(true); };
  const activeRoleDef = ROLE_OPTIONS.find(r => r.id === effectiveRole) || ROLE_OPTIONS[0];

  if (loading) return <div className="page-content" style={{ display: "flex", justifyContent: "center", padding: 60 }}><Loader2 style={{ width: 24, height: 24, animation: "spin 0.8s linear infinite", color: "var(--blue)" }} /></div>;

  // Calendar helpers
  const calYear = calDate.getFullYear(), calMonth = calDate.getMonth();
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const firstDow = new Date(calYear, calMonth, 1).getDay();
  const monthLabel = calDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const calDays = []; for (let i = 0; i < firstDow; i++) calDays.push(null); for (let i = 1; i <= daysInMonth; i++) calDays.push(i);
  const getDemosForDate = (day) => { if (!day) return []; const ds = `${calYear}-${String(calMonth+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`; return activeDemos.filter(d => d.date === ds); };

  // Analytics
  const outcomeData = Object.entries(stats?.outcomes || {}).map(([k, v]) => ({ name: (OUTCOME_OPTIONS.find(o => o.id === k)?.label || k).slice(0, 20), count: v }));
  const completedDemos = activeDemos.filter(d => d.status === "completed");
  const demoToProposal = completedDemos.filter(d => d.outcome === "requested_proposal").length;
  const demoToClose = completedDemos.filter(d => d.outcome === "highly_interested").length;

  // Demo Pipeline segments
  const upcomingDemos = activeDemos.filter(d => d.status === "confirmed" || d.status === "unconfirmed");
  const needsDemoLeads = leads.filter(l => (l.stage === "demo" || l.stage === "contacted") && !upcomingDemos.some(d => d.lead_id === l.id));
  const completedNeedingAction = activeDemos.filter(d => d.status === "completed" && !d.outcome && !d.client_outcome_id);

  // ── Aug 2026 · Scenario 1 — "past demos awaiting outcome" ───────────
  // Every demo where the current rep is the host_rep_id AND the demo
  // date has passed AND the row is still 'confirmed' (rep forgot to
  // click Complete/No-show). The banner is intentionally NON-DISMISSIBLE
  // — the rep must resolve each one before the count drops to zero,
  // making Complete/No-show mandatory. Super-admins see the same count
  // for THEIR own past demos; managers still see the team roll-up
  // separately in the existing attention-banner block.
  const myPastConfirmedDemos = activeDemos.filter(d =>
    (d.host_rep_id === user?.id || d.host_user_id === user?.id) &&
    (d.status === "confirmed" || d.status === "unconfirmed") &&
    d.date && d.date < today
  ).sort((a, b) => (a.date + (a.time || "")).localeCompare(b.date + (b.time || "")));

  // Reps for team schedule
  const reps = [...new Set(activeDemos.map(d => d.host_rep_name).filter(Boolean))].map(name => ({ name, demos: activeDemos.filter(d => d.host_rep_name === name) }));
  const unassignedDemos = activeDemos.filter(d => !d.host_rep_name && d.status !== "cancelled" && d.status !== "completed" && d.status !== "no_show");

  // Team Schedule dynamic date range driven by teamView + teamAnchor
  const _pad = n => String(n).padStart(2, "0");
  const _ymd = d => `${d.getFullYear()}-${_pad(d.getMonth()+1)}-${_pad(d.getDate())}`;
  const _startOfWeek = d => { const x = new Date(d); x.setHours(0,0,0,0); x.setDate(x.getDate() - x.getDay()); return x; };
  const _endOfWeek = d => { const x = _startOfWeek(d); x.setDate(x.getDate() + 6); return x; };
  const _startOfMonth = d => new Date(d.getFullYear(), d.getMonth(), 1);
  const _endOfMonth = d => new Date(d.getFullYear(), d.getMonth() + 1, 0);

  const scheduleDates = (() => {
    const arr = [];
    if (teamView === "day") {
      const d = new Date(teamAnchor); d.setHours(0,0,0,0);
      arr.push(d);
    } else if (teamView === "month") {
      const s = _startOfMonth(teamAnchor), e = _endOfMonth(teamAnchor);
      for (let i = 0; i <= (e.getDate() - s.getDate()); i++) { const d = new Date(s); d.setDate(s.getDate() + i); arr.push(d); }
    } else {
      const s = _startOfWeek(teamAnchor);
      for (let i = 0; i < 7; i++) { const d = new Date(s); d.setDate(s.getDate() + i); arr.push(d); }
    }
    return arr;
  })();
  const scheduleDateStrs = scheduleDates.map(_ymd);
  const rangeStart = scheduleDateStrs[0];
  const rangeEnd = scheduleDateStrs[scheduleDateStrs.length - 1];
  const inRange = (ds) => ds && ds >= rangeStart && ds <= rangeEnd;

  const rangeLabel = teamView === "day"
    ? scheduleDates[0].toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", year: "numeric" })
    : teamView === "month"
      ? teamAnchor.toLocaleDateString("en-US", { month: "long", year: "numeric" })
      : (() => { const s = scheduleDates[0], e = scheduleDates[6];
          const sameMonth = s.getMonth() === e.getMonth();
          return sameMonth
            ? `${s.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${e.getDate()}, ${e.getFullYear()}`
            : `${s.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${e.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`; })();

  const shiftAnchor = (dir) => {
    const d = new Date(teamAnchor);
    if (teamView === "day") d.setDate(d.getDate() + dir);
    else if (teamView === "month") d.setMonth(d.getMonth() + dir);
    else d.setDate(d.getDate() + (7 * dir));
    setTeamAnchor(d);
  };

  // Counts and capacity scoped to the currently-visible range
  const rangeDemos = activeDemos.filter(d => inRange(d.date));
  const rangeUnassigned = unassignedDemos.filter(d => inRange(d.date));
  const capThreshold = teamView === "day" ? 2 : teamView === "month" ? 32 : 8; // 2/day, 8/week, ~32/month
  const capNear = teamView === "day" ? 2 : teamView === "month" ? 28 : 7;

  // Day column width depends on view: day=full flex, week=1fr, month=narrow (~36px min)
  const dayColTemplate = teamView === "month"
    ? `160px repeat(${scheduleDates.length}, minmax(36px, 1fr))`
    : teamView === "day"
      ? `160px 1fr`
      : `160px repeat(7, 1fr)`;

  return (
    <div data-testid={isClient ? "client-bookings-page" : "demo-bookings-page"}>
      {/* Page Header */}
      <div style={{ background: "#fff", borderBottom: "0.5px solid #eee", padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div><div data-testid="bookings-page-title" style={{ fontSize: 22, fontWeight: 500, color: "#111" }}>{isClient ? "Client Bookings" : "Lead Bookings"}</div><div style={{ fontSize: 13, color: "#888" }}>{isClient ? "Demos booked with existing EHI clients" : "Manage, schedule, and track all demo activity"}</div></div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {isAdminOrSuper && <div style={{ position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 12, color: "#888" }}>Previewing as:</span>
              <button onClick={() => setShowRoleDropdown(!showRoleDropdown)} data-testid="role-preview-btn" style={{ display: "flex", alignItems: "center", gap: 8, border: "1.5px solid #2563eb", borderRadius: 8, padding: "7px 14px", background: "#fff", cursor: "pointer", minWidth: 200, fontFamily: "inherit" }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: activeRoleDef.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 500, color: activeRoleDef.color }}>{activeRoleDef.initials}</div>
                <span style={{ fontSize: 13, fontWeight: 500, color: "#2563eb" }}>{activeRoleDef.label}</span>
                <ChevronDown style={{ width: 12, height: 12, color: "#2563eb", marginLeft: "auto" }} />
              </button>
            </div>
            {showRoleDropdown && <div style={{ position: "absolute", right: 0, top: "100%", marginTop: 4, background: "#fff", border: "0.5px solid #ddd", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.10)", padding: 6, minWidth: 260, zIndex: 50 }} data-testid="role-dropdown-panel">
              <div style={{ padding: "8px 10px 6px", fontSize: 11, color: "#888", textTransform: "uppercase" }}>Preview as a different role</div>
              {ROLE_OPTIONS.map(r => (
                <button key={r.id} onClick={() => { setPreviewRole(r.id === (user?.role || "super_admin") ? "" : r.id); setShowRoleDropdown(false); }} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "9px 10px", borderRadius: 6, border: "none", background: "transparent", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }} onMouseOver={e => e.currentTarget.style.background = "#f5f5f5"} onMouseOut={e => e.currentTarget.style.background = "transparent"}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: r.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 500, color: r.color, flexShrink: 0 }}>{r.initials}</div>
                  <div><div style={{ fontSize: 13, fontWeight: 500 }}>{r.label}</div><div style={{ fontSize: 11, color: "#888" }}>{r.desc}</div></div>
                  {effectiveRole === r.id && <Check style={{ width: 14, height: 14, color: "#2563eb", marginLeft: "auto" }} />}
                </button>
              ))}
              <div style={{ borderTop: "0.5px solid #eee", padding: "8px 10px", fontSize: 11, fontStyle: "italic", color: "#aaa" }}>Role preview only — permissions unchanged</div>
            </div>}
          </div>}
          {canSchedule && <button onClick={() => { setPrefillLead(null); setShowSchedule(true); }} className="btn btn-primary" style={{ fontSize: 13, padding: "8px 18px" }} data-testid={isClient ? "book-client-demo-btn" : "schedule-demo-btn"}><Plus style={{ width: 14, height: 14 }} /> {isClient ? "Book client demo" : "Schedule Demo"}</button>}
          {!isClient && <button onClick={() => setShowLeadFilters(true)} className="btn"
            data-testid="open-lead-filters-btn"
            style={leadFilterCount > 0
              ? { background: "var(--blue-light)", color: "var(--blue)", borderColor: "var(--blue)", fontSize: 12, padding: "8px 14px", display: "flex", alignItems: "center", gap: 4 }
              : { fontSize: 12, padding: "8px 14px", display: "flex", alignItems: "center", gap: 4 }}
            title="Same Leads filter sidebar — slices demos by EHR / specialty / source / Medicare buckets / state. State persists across Leads, Contacts and Bookings.">
            <SlidersHorizontal style={{ width: 13, height: 13 }} /> Lead filters {leadFilterCount > 0 && <span className="tag tag-blue" style={{ fontSize: 9, marginLeft: 3 }}>{leadFilterCount}</span>}
          </button>}
          <button onClick={() => navigate("/calendar")} className="btn" style={{ fontSize: 12, padding: "8px 14px", display: "flex", alignItems: "center", gap: 4 }} data-testid="view-in-calendar-btn"><Calendar style={{ width: 13, height: 13 }} /> View in Calendar</button>
        </div>
      </div>
      {previewRole && <div style={{ background: "#FAEEDA", borderBottom: "1px solid #FAC775", padding: "8px 16px", fontSize: 12, color: "#854F0B", display: "flex", alignItems: "center", justifyContent: "space-between" }} data-testid="role-preview-banner"><span>Role preview active — viewing as <strong>{activeRoleDef.label}</strong>. Permissions unchanged.</span><button onClick={() => setPreviewRole("")} style={{ background: "none", border: "none", color: "#854F0B", fontSize: 12, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline" }}>Exit preview</button></div>}

      {/* Tabs */}
      <div style={{ background: "#fff", borderBottom: "0.5px solid #eee", padding: "0 24px", display: "flex", gap: 0 }} data-testid="demo-tabs">
        {visibleTabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`demo-tab-${t.id}`} style={{ padding: "12px 18px", fontSize: 13, fontWeight: 500, color: activeTab === t.id ? "#2563eb" : "#888", borderBottom: activeTab === t.id ? "2px solid #2563eb" : "2px solid transparent", cursor: "pointer", background: "none", border: "none", borderBottomWidth: 2, borderBottomStyle: "solid", borderBottomColor: activeTab === t.id ? "#2563eb" : "transparent", fontFamily: "inherit", whiteSpace: "nowrap" }}>{t.label}</button>
        ))}
      </div>

      <div className="page-content" style={{ padding: "16px 24px" }}>

        {/* ══ OVERVIEW ══ */}
        {activeTab === "overview" && (<>
          {/* AI Demo Optimizer Panel (2.3) — lead demos only */}
          {!isClient && <div style={{ background: "#0F2942", borderRadius: 10, padding: "14px 18px", marginBottom: 16 }} data-testid="ai-demo-panel">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Sparkles style={{ width: 12, height: 12, color: "#93C5FD" }} />
                <span style={{ fontSize: 13, fontWeight: 500, color: "#fff" }}>Claude AI — Demo Optimizer</span>
              </div>
              <span style={{ fontSize: 10, color: "#8BA5C0" }}>Updated just now</span>
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#34c759", marginTop: 4, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: "#CBD5E0", lineHeight: 1.7 }}>Best booking times this week: Tuesday 10 AM and Thursday 2 PM show highest attendance rates</span>
              </div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#ff9500", marginTop: 4, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: "#CBD5E0", lineHeight: 1.7 }}>{reps.length > 0 ? `${reps[0]?.name || "Top rep"} is near capacity — consider redistributing demos` : "Rep capacity looks balanced this week"}</span>
              </div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#ff3b30", marginTop: 4, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: "#CBD5E0", lineHeight: 1.7 }}>{stats?.reschedule_rate > 15 ? `Reschedule rate is ${stats.reschedule_rate}% — consider sending prep materials earlier` : "Reschedule rate is healthy"}</span>
              </div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#0071e3", marginTop: 4, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: "#CBD5E0", lineHeight: 1.7 }}>Demos with MDNet EHR product have {stats?.show_rate || 85}% show rate — highest among all products</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button onClick={() => setActiveTab("analytics")} style={{ background: "rgba(255,255,255,0.10)", color: "#fff", border: "0.5px solid rgba(255,255,255,0.20)", borderRadius: 8, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4 }}>
                View full analytics
              </button>
              <button onClick={() => toast.info("AI Schedule Optimizer opening...")} style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4 }}>
                <Sparkles style={{ width: 10, height: 10 }} /> Optimize schedule
              </button>
            </div>
          </div>}
          {stats && <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 10, marginBottom: 16 }} data-testid="overview-stats">
            {[{ label: "Demos This Week", value: stats.demos_this_week, sub: `${stats.demos_this_week - stats.demos_prev_week >= 0 ? "+" : ""}${stats.demos_this_week - stats.demos_prev_week} vs last week`, subColor: stats.demos_this_week >= stats.demos_prev_week ? "#3B6D11" : "#A32D2D" },
              { label: "Demos Today", value: stats.demos_today, sub: `${stats.today_upcoming} upcoming · ${stats.today_completed} completed` },
              { label: "Booked This Month", value: stats.booked_this_month, sub: `vs ${stats.booked_last_month} last month` },
              { label: "Show Rate", value: `${stats.show_rate}%`, sub: "Attended / Scheduled" },
              { label: "Reschedule Rate", value: `${stats.reschedule_rate}%`, sub: "Rescheduled / Total", subColor: stats.reschedule_rate > 25 ? "#854F0B" : undefined },
              { label: "No-Show Rate", value: `${stats.no_show_rate}%`, sub: "No show / Total", subColor: stats.no_show_rate > 15 ? "#A32D2D" : undefined },
            ].map((s, i) => (<div key={i} style={{ background: "#f9f9f9", borderRadius: 8, padding: "12px 16px" }}><div style={{ fontSize: 10, textTransform: "uppercase", color: "#888", letterSpacing: "0.3px" }}>{s.label}</div><div style={{ fontSize: 24, fontWeight: 500, marginTop: 2 }}>{s.value}</div><div style={{ fontSize: 11, color: s.subColor || "#888", marginTop: 2 }}>{s.sub}</div></div>))}
          </div>}
          {stats && stats.needs_attention > 0 && <div style={{ background: "#FFF8F0", border: "0.5px solid #FAC775", borderRadius: 8, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "flex-start", gap: 12 }} data-testid="attention-banner">
            <AlertTriangle style={{ width: 16, height: 16, color: "#BA7517", flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 500, color: "#854F0B" }}>{stats.needs_attention} demos need attention</div><div style={{ fontSize: 11, color: "#854F0B", marginTop: 4 }}>{stats.unconfirmed > 0 && <div>· {stats.unconfirmed} demos unconfirmed</div>}{stats.heavy_reschedules > 0 && <div>· {stats.heavy_reschedules} demos rescheduled 3+ times</div>}{stats.no_prep_sent > 0 && <div>· {stats.no_prep_sent} demos with no prep materials sent</div>}</div></div>
            <button onClick={() => { setActiveTab("all-demos"); setStatusFilter("unconfirmed"); }} style={{ color: "#854F0B", fontSize: 12, textDecoration: "underline", background: "none", border: "none", cursor: "pointer", whiteSpace: "nowrap", fontFamily: "inherit" }}>View all</button>
          </div>}

          {/* Scenario 1 · Past demos awaiting outcome — MANDATORY nudge.
              Non-dismissible; count only drops as the rep resolves each row.
              Anchors to the section below (id=past-demos-triage) on click. */}
          {myPastConfirmedDemos.length > 0 && (
            <div
              data-testid="past-demos-banner"
              style={{
                background: "linear-gradient(90deg, #FEECEC 0%, #FDF6F6 100%)",
                border: "1px solid #E24B4A",
                borderLeft: "4px solid #A32D2D",
                borderRadius: 8,
                padding: "14px 18px",
                marginBottom: 16,
                display: "flex",
                alignItems: "center",
                gap: 14,
              }}
            >
              <AlertTriangle style={{ width: 20, height: 20, color: "#A32D2D", flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#A32D2D" }}>
                  {myPastConfirmedDemos.length} past {myPastConfirmedDemos.length === 1 ? "demo needs" : "demos need"} an outcome
                </div>
                <div style={{ fontSize: 11.5, color: "#7A1D1D", marginTop: 3 }}>
                  Every past demo must be marked either <strong>Completed</strong> or <strong>No-show</strong>. This is required for accurate reporting on Reports → Rep Performance.
                </div>
              </div>
              <button
                onClick={() => {
                  const el = document.getElementById("past-demos-triage");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                data-testid="past-demos-jump"
                style={{
                  background: "#A32D2D",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 14px",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  fontFamily: "inherit",
                }}
              >
                Close them out ↓
              </button>
            </div>
          )}
          {/* Scenario 1 · Past demos triage — placed above "Today's demos"
              so the rep sees it first when they land on the page.  */}
          {myPastConfirmedDemos.length > 0 && (
            <div id="past-demos-triage" style={{ marginBottom: 24 }}>
              <div style={{ ...SL, marginBottom: 10, color: "#A32D2D" }}>
                Past demos awaiting outcome ({myPastConfirmedDemos.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {myPastConfirmedDemos.map(d => {
                  const isOpen = !!expandedRecall[d.id];
                  const rc     = recallCache[d.id];
                  return (
                  <div
                    key={d.id}
                    data-testid={`past-demo-triage-${d.id}`}
                    style={{
                      background: "#fff",
                      border: "0.5px solid #F5C6C5",
                      borderLeft: "3px solid #E24B4A",
                      borderRadius: 8,
                      overflow: "hidden",
                    }}
                  >
                    <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 14 }}>
                      <div style={{ width: 96, flexShrink: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{fmtDate(d.date)}</div>
                        <div style={{ fontSize: 11, color: "#888" }}>{fmt12(d.time)} · {d.duration || 45}m</div>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{ fontSize: 13, fontWeight: 500, cursor: "pointer", color: "#2563eb", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          onClick={() => setDemoDetail(d)}
                        >
                          {demoName(d)}
                        </div>
                        <div style={{ fontSize: 11, color: "#888", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {demoContact(d)}{d.product || d.demo_type ? ` · ${d.product || d.demo_type}` : ""}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 6, flexShrink: 0, alignItems: "center" }}>
                        <button
                          onClick={() => toggleRecall(d.id)}
                          data-testid={`past-demo-expand-${d.id}`}
                          title={isOpen ? "Hide recall aids" : "Show recall aids (recording, transcript, notes)"}
                          style={{ background: isOpen ? "#F1F5F9" : "#fff", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 6, padding: "6px 10px", fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5 }}
                        >
                          {isOpen ? "Hide" : "Recall"} {isOpen ? "▲" : "▼"}
                        </button>
                        <button
                          onClick={() => setShowOutcome(d)}
                          data-testid={`past-demo-complete-${d.id}`}
                          title="Log demo outcome and mark completed"
                          style={{ background: "#15803d", color: "#fff", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                        >
                          Completed
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Mark this demo as No-show?\n\n${demoName(d)} — ${fmtDate(d.date)} ${fmt12(d.time)}`)) {
                              markNoShow(d.id);
                            }
                          }}
                          data-testid={`past-demo-no-show-${d.id}`}
                          title="Mark as no-show — lead did not attend"
                          style={{ background: "#fff", color: "#A32D2D", border: "1px solid #E24B4A", borderRadius: 6, padding: "6px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                        >
                          No-show
                        </button>
                      </div>
                    </div>
                    {isOpen && (
                      <RecallPane
                        recall={rc?.data}
                        loading={rc?.loading}
                        error={rc?.error}
                        booking={d}
                        testIdPrefix={`recall-${d.id}`}
                      />
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ ...SL, marginBottom: 10 }}>Today's demos</div>
          {todayDemos.length === 0 ? <div style={{ border: "0.5px dashed #ddd", borderRadius: 8, padding: 24, textAlign: "center", color: "#888", fontSize: 13, marginBottom: 20 }}>No demos scheduled for today.{canSchedule && <div style={{ marginTop: 8 }}><button onClick={() => setShowSchedule(true)} className="btn btn-primary" style={{ fontSize: 12 }}><Plus style={{ width: 12, height: 12 }} /> Schedule Demo</button></div>}</div> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
              {todayDemos.map(d => { const sc = STATUS_COLORS[d.status] || STATUS_COLORS.confirmed; return (
                <div key={d.id} style={{ background: "#fff", border: "0.5px solid #eee", borderRadius: 8, padding: "12px 16px", display: "flex", alignItems: "flex-start", gap: 14, borderLeft: `3px solid ${sc.border}` }} data-testid={`today-demo-${d.id}`}>
                  <div style={{ width: 60, flexShrink: 0 }}><div style={{ fontSize: 13, fontWeight: 500 }}>{fmt12(d.time)}</div><div style={{ fontSize: 11, color: "#888" }}>{d.duration || 45} min</div></div>
                  <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 500, cursor: "pointer", color: "#2563eb" }} onClick={() => setDemoDetail(d)}>{demoName(d)}</div><div style={{ fontSize: 11, color: "#888" }}>{demoContact(d)} · {d.product || d.demo_type}</div></div>
                  <div style={{ flexShrink: 0 }}><div style={{ fontSize: 10, textTransform: "uppercase", color: "#888" }}>Host</div><div style={{ fontSize: 12 }}>{d.host_rep_name || <span style={{ color: "#A32D2D" }}>Unassigned</span>}</div></div>
                  <div style={{ flexShrink: 0, width: 100, textAlign: "right" }}><StatusBadge status={d.status} /><div style={{ marginTop: 6 }}>
                    {d.status === "unconfirmed" && <button onClick={() => confirmDemo(d.id)} style={{ background: "#EAF3DE", color: "#3B6D11", border: "none", borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Confirm</button>}
                    {d.status === "confirmed" && (
                      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        <button onClick={() => joinDemo(d)} data-testid={`join-demo-${d.id}`} title="Open meeting in a new tab" style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Join demo</button>
                        <button onClick={() => setShowOutcome(d)} data-testid={`log-outcome-${d.id}`} title="Log outcome without opening meeting" style={{ background: "#fff", color: "#3C3489", border: "0.5px solid #CECBF6", borderRadius: 6, padding: "4px 8px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Log</button>
                      </div>
                    )}
                  </div></div>
                </div>); })}
            </div>
          )}
          <div style={{ ...SL, marginTop: 20, marginBottom: 10 }}>Recent demo activity</div>
          {feed.slice(0, 8).map((f, i) => (<div key={i} style={{ display: "flex", gap: 10, padding: "8px 0", borderBottom: "0.5px solid #eee" }}><div style={{ width: 8, height: 8, borderRadius: "50%", background: f.color || "#888", marginTop: 4, flexShrink: 0 }} /><div><div style={{ fontSize: 12 }}>{f.text}</div><div style={{ fontSize: 11, color: "#888" }}>{f.time ? new Date(f.time).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}</div></div></div>))}
          {feed.length === 0 && <div style={{ fontSize: 12, color: "#888", padding: 12, textAlign: "center" }}>No recent demo activity.</div>}
        </>)}

        {/* ══ CALENDAR ══ */}
        {activeTab === "calendar" && (<>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button onClick={() => setCalDate(new Date(calYear, calMonth - 1, 1))} className="btn" style={{ padding: "5px 12px", fontSize: 12 }}><ChevronLeft style={{ width: 14, height: 14 }} /></button>
              <button onClick={() => setCalDate(new Date())} className="btn" style={{ padding: "5px 12px", fontSize: 12 }}>Today</button>
              <button onClick={() => setCalDate(new Date(calYear, calMonth + 1, 1))} className="btn" style={{ padding: "5px 12px", fontSize: 12 }}><ChevronRight style={{ width: 14, height: 14 }} /></button>
              <span style={{ fontSize: 15, fontWeight: 500, marginLeft: 8 }}>{monthLabel}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ display: "flex", gap: 0 }}>{["month", "week", "day"].map((v, i) => (<button key={v} onClick={() => setCalView(v)} style={{ padding: "5px 12px", fontSize: 12, cursor: "pointer", fontFamily: "inherit", background: calView === v ? "#2563eb" : "#fff", color: calView === v ? "#fff" : "#888", border: "0.5px solid #ddd", borderRadius: i === 0 ? "8px 0 0 8px" : i === 2 ? "0 8px 8px 0" : 0 }}>{v.charAt(0).toUpperCase() + v.slice(1)}</button>))}</div>
              <select value={calTz} onChange={e => { setCalTz(e.target.value); toast.success(`Calendar showing ${e.target.value}`); }} style={{ padding: "5px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit" }} data-testid="tz-selector">
                <option value="ET">ET</option><option value="CT">CT</option><option value="MT">MT</option><option value="PT">PT</option>
              </select>
            </div>
          </div>
          {/* Month View */}
          {calView === "month" && <div style={{ border: "0.5px solid #eee", borderRadius: 8, overflow: "hidden" }} data-testid="calendar-grid">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
              {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (<div key={d} style={{ padding: 8, fontSize: 11, textTransform: "uppercase", fontWeight: 500, color: "#888", background: "#f9f9f9", borderBottom: "0.5px solid #eee", textAlign: "center" }}>{d}</div>))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
              {calDays.map((day, i) => {
                const dayDemos = getDemosForDate(day);
                const dateStr = day ? `${calYear}-${String(calMonth+1).padStart(2,"0")}-${String(day).padStart(2,"0")}` : "";
                const isToday = dateStr === today;
                const isBlocked = (settings?.blocked_dates || []).some(b => b.start_date <= dateStr && (b.end_date || b.start_date) >= dateStr);
                return (<div key={i} style={{ borderRight: "0.5px solid #eee", borderBottom: "0.5px solid #eee", padding: 6, minHeight: 90, background: isBlocked ? "#FFF5F5" : isToday ? "#f0f7ff" : "#fff", cursor: day ? "pointer" : "default", opacity: isBlocked ? 0.6 : 1 }} onClick={() => day && canSchedule && !isBlocked && setShowSchedule(true)}>
                  {day && <div style={{ fontSize: 12, fontWeight: 500, color: isBlocked ? "#A32D2D" : "#111", marginBottom: 4 }}>{day}{isBlocked && <span style={{ fontSize: 8, color: "#A32D2D", marginLeft: 3 }}>BLOCKED</span>}</div>}
                  {dayDemos.slice(0, 3).map(d => { const sc = STATUS_COLORS[d.status] || STATUS_COLORS.confirmed; return <div key={d.id} style={{ borderRadius: 4, padding: "2px 6px", fontSize: 11, fontWeight: 500, marginBottom: 2, background: sc.bg, color: sc.color, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 4, textDecoration: d.status === "cancelled" ? "line-through" : "none", cursor: "pointer" }} onClick={e => { e.stopPropagation(); setDemoDetail(d); }}><span>{fmt12(d.time)}</span><span style={{ color: "#888" }}>·</span><span>{trunc(demoName(d), 14)}</span></div>; })}
                  {dayDemos.length > 3 && <div style={{ fontSize: 10, color: "#888", background: "#f5f5f5", borderRadius: 4, padding: "1px 4px", display: "inline-block", cursor: "pointer" }} title={dayDemos.slice(3).map(d => `${fmt12(d.time)} ${demoName(d)}`).join("\n")}>+{dayDemos.length - 3} more</div>}
                </div>);
              })}
            </div>
          </div>}

          {/* Week View */}
          {calView === "week" && (() => {
            const weekStart = new Date(calDate); weekStart.setDate(weekStart.getDate() - weekStart.getDay());
            const hours = []; for (let h = 8; h <= 19; h++) { hours.push(h); if (h < 19) hours.push(h + 0.5); }
            return (
              <div style={{ border: "0.5px solid #eee", borderRadius: 8, overflow: "hidden" }} data-testid="week-view">
                <div style={{ display: "grid", gridTemplateColumns: "60px repeat(7, 1fr)" }}>
                  <div style={{ background: "#f9f9f9", borderBottom: "0.5px solid #eee", borderRight: "0.5px solid #eee", padding: 8 }} />
                  {Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); const isT = d.toISOString().slice(0,10) === today; return (
                    <div key={i} style={{ textAlign: "center", padding: 8, background: isT ? "#f0f7ff" : "#f9f9f9", borderBottom: "0.5px solid #eee", borderRight: "0.5px solid #eee" }}>
                      <div style={{ fontSize: 11, color: "#888" }}>{d.toLocaleDateString("en-US", { weekday: "short" })}</div>
                      <div style={{ fontSize: 15, fontWeight: 500 }}>{d.getDate()}</div>
                    </div>); })}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "60px repeat(7, 1fr)", position: "relative" }}>
                  {hours.map((h, hi) => {
                    const isHalf = h % 1 !== 0; const hour = Math.floor(h);
                    const label = !isHalf ? `${hour > 12 ? hour - 12 : hour === 0 ? 12 : hour} ${hour >= 12 ? "PM" : "AM"}` : "";
                    return (
                      <div key={hi} style={{ display: "contents" }}>
                        <div style={{ padding: "0 8px", textAlign: "right", fontSize: 11, color: "#888", borderRight: "0.5px solid #eee", height: 30, display: "flex", alignItems: "center", justifyContent: "flex-end", borderBottom: isHalf ? "0.5px dashed #f5f5f5" : "0.5px solid #eee" }}>{label}</div>
                        {Array.from({ length: 7 }, (_, di) => {
                          const d = new Date(weekStart); d.setDate(d.getDate() + di);
                          const dateStr = d.toISOString().slice(0,10);
                          const slotDemos = !isHalf ? activeDemos.filter(dm => dm.date === dateStr && dm.time && parseInt(dm.time.split(":")[0]) === hour) : [];
                          return (
                            <div key={di} style={{ borderRight: "0.5px solid #eee", borderBottom: isHalf ? "0.5px dashed #f5f5f5" : "0.5px solid #eee", padding: 1, height: 30, background: d.toISOString().slice(0,10) === today ? "#f0f7ff" : "#fff" }}>
                              {slotDemos.map(dm => { const sc = STATUS_COLORS[dm.status] || STATUS_COLORS.confirmed; return (
                                <div key={dm.id} onClick={() => setShowOutcome(dm)} style={{ background: sc.bg, color: sc.color, borderLeft: `3px solid ${sc.border}`, borderRadius: 4, padding: "2px 4px", fontSize: 9, cursor: "pointer", marginBottom: 1, overflow: "hidden" }}>
                                  <div style={{ fontWeight: 600 }}>{fmt12(dm.time)}</div>
                                  <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{trunc(demoName(dm), 12)}</div>
                                </div>); })}
                            </div>);
                        })}
                      </div>);
                  })}
                </div>
              </div>);
          })()}

          {/* Day View */}
          {calView === "day" && (() => {
            const dayStr = calDate.toISOString().slice(0, 10);
            const dayDemos = activeDemos.filter(d => d.date === dayStr);
            const hours = []; for (let h = 8; h <= 19; h++) hours.push(h);
            return (
              <div style={{ border: "0.5px solid #eee", borderRadius: 8, overflow: "hidden" }} data-testid="day-view">
                <div style={{ background: "#f9f9f9", padding: "10px 16px", borderBottom: "0.5px solid #eee", fontSize: 14, fontWeight: 500 }}>
                  {calDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
                  <span style={{ fontSize: 12, color: "#888", marginLeft: 8 }}>{dayDemos.length} demo{dayDemos.length !== 1 ? "s" : ""}</span>
                </div>
                {hours.map(h => {
                  const label = `${h > 12 ? h - 12 : h === 0 ? 12 : h}:00 ${h >= 12 ? "PM" : "AM"}`;
                  const slotDemos = dayDemos.filter(dm => dm.time && parseInt(dm.time.split(":")[0]) === h);
                  return (
                    <div key={h} style={{ display: "flex", borderBottom: "0.5px solid #eee", minHeight: 70 }}>
                      <div style={{ width: 70, padding: "8px 10px", textAlign: "right", fontSize: 11, color: "#888", borderRight: "0.5px solid #eee", flexShrink: 0 }}>{label}</div>
                      <div style={{ flex: 1, padding: 6 }}>
                        {slotDemos.map(dm => { const sc = STATUS_COLORS[dm.status] || STATUS_COLORS.confirmed; return (
                          <div key={dm.id} onClick={() => setShowOutcome(dm)} style={{ background: sc.bg, borderLeft: `3px solid ${sc.border}`, borderRadius: 6, padding: "8px 12px", marginBottom: 4, cursor: "pointer" }}>
                            <div style={{ fontSize: 12, color: "#888" }}>{fmt12(dm.time)} – {fmt12((parseInt(dm.time?.split(":")[0]||0) + Math.floor((dm.duration||45)/60)) + ":" + ((parseInt(dm.time?.split(":")[1]||0) + (dm.duration||45)%60)%60))} ({dm.duration || 45} min)</div>
                            <div style={{ fontSize: 14, fontWeight: 500, color: sc.color }}>{demoName(dm)}</div>
                            <div style={{ fontSize: 12, color: "#888" }}>{demoContact(dm)} · {dm.specialty || ""}</div>
                            <div style={{ fontSize: 12, marginTop: 2 }}>Host: {dm.host_rep_name || <span style={{ color: "#A32D2D" }}>Unassigned</span>}</div>
                            {dm.product && <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>Products: {dm.product}</div>}
                            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                              <button onClick={e => { e.stopPropagation(); setShowOutcome(dm); }} style={{ border: "0.5px solid #eee", borderRadius: 6, padding: "3px 8px", fontSize: 11, background: "#fff", cursor: "pointer", fontFamily: "inherit" }}>Edit</button>
                              <button onClick={e => { e.stopPropagation(); cancelDemo(dm.id); }} style={{ border: "0.5px solid #F7C1C1", borderRadius: 6, padding: "3px 8px", fontSize: 11, background: "#fff", cursor: "pointer", fontFamily: "inherit", color: "#A32D2D" }}>Cancel</button>
                            </div>
                          </div>); })}
                      </div>
                    </div>);
                })}
              </div>);
          })()}
        </>)}

        {/* ══ ALL DEMOS ══ */}
        {activeTab === "all-demos" && (<>
          <div style={{ background: "#fff", border: "0.5px solid #eee", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 14 }} data-testid="demos-filter-bar">
            <div style={{ position: "relative" }}><Search style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", width: 12, height: 12, color: "#888" }} /><input value={searchFilter} onChange={e => setSearchFilter(e.target.value)} placeholder="Filter scheduled demos by lead, contact, rep…" style={{ paddingLeft: 28, padding: "7px 10px 7px 28px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit", width: 260 }} data-testid="demo-search" title="This filters scheduled demos only. To schedule a new demo for a lead, click + New Demo." /></div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ padding: "7px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit" }} data-testid="demo-status-filter"><option value="all">Status: All</option>{Object.entries(STATUS_COLORS).filter(([, v]) => !v._alias).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select>
            <span style={{ fontSize: 12, color: "#888", marginLeft: "auto" }}>{filteredDemos.length} demos</span>
          </div>
          <div style={{ border: "0.5px solid #eee", borderRadius: 8, overflow: "hidden" }} data-testid="demos-table">
            <div style={{ display: "grid", gridTemplateColumns: "4px 200px 130px 150px 110px 90px 70px 130px 90px 80px", gap: 0, padding: "10px 14px", background: "#f7f7f7", borderBottom: "0.5px solid #eee", fontSize: 9, fontWeight: 700, color: "#888", textTransform: "uppercase" }}><span /><span>Lead / Practice</span><span>Contact</span><span>Date & Time</span><span>Host</span><span>Status</span><span>Demo #</span><span>Products</span><span>Outcome</span><span>Actions</span></div>
            {filteredDemos.map(d => { const sc = STATUS_COLORS[d.status] || STATUS_COLORS.confirmed; const outDef = OUTCOME_OPTIONS.find(o => o.id === d.outcome); const prods = (d.product || "").split(",").map(s => s.trim()).filter(Boolean); return (
              <div key={d.id} style={{ display: "grid", gridTemplateColumns: "4px 200px 130px 150px 110px 90px 70px 130px 90px 80px", gap: 0, padding: "10px 14px", borderBottom: "0.5px solid #eee", borderLeft: `4px solid ${sc.border}`, alignItems: "center" }} data-testid={`demo-row-${d.id}`}>
                <span />
                <div><div style={{ fontSize: 13, fontWeight: 650, cursor: "pointer", color: "#111" }} onClick={() => openRecord(d)}>{demoName(d)}</div><div style={{ fontSize: 11, color: "#888" }}>{d.city || d.specialty || ""}</div></div>
                <div><div style={{ fontSize: 12, fontWeight: 500 }}>{demoContact(d)}</div><div style={{ fontSize: 11, color: "#888" }}>{d.specialty || ""}</div></div>
                <div><div style={{ fontSize: 12, fontWeight: 500 }}>{fmtDate(d.date)}</div><div style={{ fontSize: 11, color: "#888" }}>{fmt12(d.time)}</div></div>
                <div style={{ fontSize: 12 }}>{d.host_rep_name || <span style={{ color: "#A32D2D" }}>Unassigned</span>}</div>
                <StatusBadge status={d.status} />
                <div style={{ fontSize: 12 }}>{d.reschedule_count > 0 ? <span style={{ color: d.reschedule_count >= 3 ? "#A32D2D" : "#854F0B" }}>#{d.reschedule_count + 1} ↺</span> : "#1"}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>{prods.slice(0, 2).map((p, i) => <span key={i} style={{ padding: "1px 6px", borderRadius: 10, fontSize: 9, fontWeight: 500, background: "#EEEDFE", color: "#3C3489" }}>{trunc(p, 16)}</span>)}{prods.length > 2 && <span style={{ padding: "1px 4px", borderRadius: 10, fontSize: 9, background: "#f5f5f5", color: "#888" }}>+{prods.length-2}</span>}{prods.length === 0 && <span style={{ fontSize: 10, color: "#888" }}>—</span>}</div>
                <div>{outDef ? <span style={{ padding: "2px 6px", borderRadius: 10, fontSize: 9, fontWeight: 500, background: outDef.bg, color: outDef.color }}>{trunc(outDef.label, 16)}</span> : d.client_outcome_id ? <span data-testid={`demo-client-outcome-${d.id}`} style={{ padding: "2px 6px", borderRadius: 10, fontSize: 9, fontWeight: 500, background: "#E6F1FB", color: "#175cd3" }}>Outcome logged</span> : <span style={{ fontSize: 10, color: "#888" }}>{d.status === "completed" ? "Not logged" : "—"}</span>}</div>
                <div data-testid={`demo-row-actions-${d.id}`} style={{ display: readOnlyClient ? "none" : "flex", gap: 2 }}>{(d.status === "confirmed" || d.status === "completed" || (demoClientId(d) && !["cancelled", "canceled"].includes(d.status))) && <button onClick={() => setShowOutcome(d)} data-testid={`demo-eye-${d.id}`} style={{ background: "none", border: "none", cursor: "pointer", padding: 3, color: "#888" }} title="Log outcome"><Eye style={{ width: 12, height: 12 }} /></button>}{rescheduleEligible(d) ? <button onClick={() => setShowReschedule(d)} data-testid={`reschedule-btn-${d.id}`} style={{ background: "none", border: "none", cursor: "pointer", padding: 3, color: "#854F0B" }} title="Reschedule"><Clock style={{ width: 12, height: 12 }} /></button> : <button disabled data-testid={`reschedule-btn-${d.id}`} style={{ background: "none", border: "none", cursor: "not-allowed", padding: 3, color: "#ccc", opacity: 0.5 }} title="Reschedule unavailable"><Clock style={{ width: 12, height: 12 }} /></button>}{d.status !== "cancelled" && d.status !== "completed" && d.status !== "no_show" && <button onClick={() => cancelDemo(d.id)} style={{ background: "none", border: "none", cursor: "pointer", padding: 3, color: "#A32D2D" }} title="Cancel"><X style={{ width: 12, height: 12 }} /></button>}</div>
              </div>); })}
            {filteredDemos.length === 0 && (
              <div style={{ padding: 32, textAlign: "center", color: "#888", fontSize: 13 }}>
                {searchFilter
                  ? <>No <strong>scheduled demos</strong> match &ldquo;{searchFilter}&rdquo;.<br /><span style={{ fontSize: 12 }}>To schedule a new demo for that lead, click <strong>+ New Demo</strong> at the top right.</span></>
                  : "No demos found."}
              </div>
            )}
          </div>
        </>)}

        {/* ══ DEMO PIPELINE ══ */}
        {activeTab === "demo-pipeline" && (<>
          <div style={{ marginBottom: 16 }}><div style={{ fontSize: 18, fontWeight: 500 }}>Demo Pipeline</div><div style={{ fontSize: 13, color: "#888" }}>All leads with demo activity — scheduled, completed, or needing a demo.</div></div>
          {/* Segment 1: Upcoming */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}><span style={{ fontSize: 13, fontWeight: 500, color: "#111" }}>Upcoming demos</span><span style={{ background: "#f5f5f5", borderRadius: 10, fontSize: 11, padding: "2px 7px", color: "#888" }}>{upcomingDemos.length}</span></div>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 10 }}>Leads with a confirmed or scheduled demo coming up</div>
            {upcomingDemos.length === 0 ? <div style={{ border: "0.5px dashed #ddd", borderRadius: 8, padding: 16, textAlign: "center", color: "#888", fontSize: 12 }}>No upcoming demos scheduled.</div> :
              upcomingDemos.map(d => (<div key={d.id} style={{ padding: "12px 16px", borderBottom: "0.5px solid #eee", display: "flex", alignItems: "center", gap: 16, borderLeft: "3px solid #1D9E75" }} onMouseOver={e => e.currentTarget.style.background="#fafafa"} onMouseOut={e => e.currentTarget.style.background="transparent"}>
                <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 13, fontWeight: 500, cursor: "pointer", color: "#2563eb" }} onClick={() => openRecord(d)}>{demoName(d)}</div><div style={{ fontSize: 11, color: "#888" }}>{demoContact(d)}</div></div>
                <div style={{ width: 90 }}><span style={{ padding: "2px 7px", borderRadius: 10, fontSize: 10, fontWeight: 500, background: "#EEEDFE", color: "#3C3489" }}>{d.lead_stage || "demo"}</span></div>
                <div style={{ width: 140 }}><div style={{ fontSize: 13, fontWeight: 500 }}>{fmtDate(d.date)} · {fmt12(d.time)}</div><div style={{ fontSize: 11, color: "#888" }}>{d.duration || 45} min</div></div>
                <div style={{ width: 110, display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 24, height: 24, borderRadius: "50%", background: "#E6F1FB", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 600, color: "#185FA5" }}>{(d.host_rep_name || "?")[0]}</div><span style={{ fontSize: 12 }}>{d.host_rep_name || <span style={{ color: "#A32D2D" }}>None</span>}</span></div>
                <div style={{ width: 90 }}><StatusBadge status={d.status} /></div>
                <div style={{ width: 160, display: "flex", gap: 4 }}><button onClick={() => setShowOutcome(d)} style={{ border: "0.5px solid #eee", borderRadius: 8, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontFamily: "inherit", background: "#fff" }}>View</button><button onClick={() => setShowReschedule(d)} disabled={!rescheduleEligible(d)} data-testid={`reschedule-pipeline-${d.id}`} style={{ border: "0.5px solid #eee", borderRadius: 8, padding: "4px 10px", fontSize: 11, cursor: rescheduleEligible(d) ? "pointer" : "not-allowed", fontFamily: "inherit", background: "#fff", opacity: rescheduleEligible(d) ? 1 : 0.45 }}>Reschedule</button></div>
              </div>))
            }
          </div>
          {/* Segment 2: Needs a Demo (lead demos only) */}
          {!isClient && <div style={{ marginBottom: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}><span style={{ fontSize: 13, fontWeight: 500, color: "#111" }}>Needs a demo</span><span style={{ background: "#FAEEDA", borderRadius: 10, fontSize: 11, padding: "2px 7px", color: "#854F0B" }}>{needsDemoLeads.length}</span></div>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 10 }}>Leads at Demo stage with no upcoming demo scheduled</div>
            {needsDemoLeads.length === 0 ? <div style={{ border: "0.5px dashed #ddd", borderRadius: 8, padding: 16, textAlign: "center", color: "#888", fontSize: 12 }}>All demo-stage leads have a demo booked.</div> :
              needsDemoLeads.map(l => (<div key={l.id} style={{ padding: "12px 16px", borderBottom: "0.5px solid #eee", display: "flex", alignItems: "center", gap: 16, borderLeft: "3px solid #BA7517" }} onMouseOver={e => e.currentTarget.style.background="#fafafa"} onMouseOut={e => e.currentTarget.style.background="transparent"}>
                <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 13, fontWeight: 500, cursor: "pointer", color: "#2563eb" }} onClick={() => navigate(`/lead-focus/${l.id}`)}>{l.title}</div><div style={{ fontSize: 11, color: "#888" }}>{l.contact_name}</div></div>
                <div style={{ width: 90 }}><span style={{ padding: "2px 7px", borderRadius: 10, fontSize: 10, fontWeight: 500, background: "#EEEDFE", color: "#3C3489" }}>{l.stage}</span></div>
                <div style={{ width: 80 }}><span style={{ fontSize: 13, fontWeight: 500, color: getLeadScore(l) >= 70 ? "#3B6D11" : getLeadScore(l) >= 40 ? "#854F0B" : "#A32D2D" }}>{getLeadScore(l)}</span></div>
                <div style={{ width: 80 }}><span style={{ fontSize: 13, fontWeight: 500, color: "#0F6E56" }}>${(l.value || 0).toLocaleString()}</span></div>
                <div style={{ width: 100 }}><span style={{ fontSize: 12, color: "#888" }}>{l.last_contact_date ? new Date(l.last_contact_date).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}</span></div>
                <div style={{ width: 140 }}><button onClick={() => openScheduleForLead(l)} style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "5px 14px", fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}>+ Schedule demo</button></div>
              </div>))
            }
          </div>}
          {/* Segment 3: Completed awaiting action */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}><span style={{ fontSize: 13, fontWeight: 500, color: "#111" }}>Demo completed — awaiting next step</span><span style={{ background: "#E6F1FB", borderRadius: 10, fontSize: 11, padding: "2px 7px", color: "#185FA5" }}>{completedNeedingAction.length}</span></div>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 10 }}>Demos done but no outcome logged or follow-up set</div>
            {completedNeedingAction.length === 0 ? <div style={{ border: "0.5px dashed #ddd", borderRadius: 8, padding: 16, textAlign: "center", color: "#888", fontSize: 12 }}>All completed demos have been followed up.</div> :
              completedNeedingAction.map(d => { const outDef = OUTCOME_OPTIONS.find(o => o.id === d.outcome); return (
                <div key={d.id} style={{ padding: "12px 16px", borderBottom: "0.5px solid #eee", display: "flex", alignItems: "center", gap: 16, borderLeft: "3px solid #2563eb" }} onMouseOver={e => e.currentTarget.style.background="#fafafa"} onMouseOut={e => e.currentTarget.style.background="transparent"}>
                  <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 13, fontWeight: 500, cursor: "pointer", color: "#2563eb" }} onClick={() => openRecord(d)}>{demoName(d)}</div><div style={{ fontSize: 11, color: "#888" }}>{demoContact(d)}</div></div>
                  <div style={{ width: 90 }}><span style={{ padding: "2px 7px", borderRadius: 10, fontSize: 10, fontWeight: 500, background: "#EEEDFE", color: "#3C3489" }}>{d.lead_stage || "demo"}</span></div>
                  <div style={{ width: 140 }}><span style={{ fontSize: 12, color: "#888" }}>Completed {fmtDate(d.completed_at?.slice(0,10) || d.date)}</span></div>
                  <div style={{ width: 130 }}>{outDef ? <span style={{ padding: "2px 6px", borderRadius: 10, fontSize: 10, fontWeight: 500, background: outDef.bg, color: outDef.color }}>{outDef.label}</span> : <span style={{ fontSize: 11, color: "#A32D2D" }}>Not logged</span>}</div>
                  <div style={{ width: 110, display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 24, height: 24, borderRadius: "50%", background: "#E6F1FB", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 600, color: "#185FA5" }}>{(d.host_rep_name || "?")[0]}</div><span style={{ fontSize: 12 }}>{d.host_rep_name || "—"}</span></div>
                  <div style={{ width: 180, display: "flex", gap: 4 }}><button onClick={() => setShowOutcome(d)} style={{ border: "0.5px solid #CECBF6", color: "#3C3489", borderRadius: 8, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontFamily: "inherit", background: "#fff" }}>Log outcome</button><button onClick={() => openScheduleForLead({ id: d.lead_id, title: d.company, contact_name: d.contact_name })} style={{ border: "0.5px solid #eee", borderRadius: 8, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontFamily: "inherit", background: "#fff" }}>Schedule follow-up</button></div>
                </div>); })
            }
          </div>
        </>)}

        {/* ══ TEAM SCHEDULE ══ */}
        {activeTab === "team-schedule" && canSeeTeam && (<>
          <div style={{ marginBottom: 6 }}><div style={{ background: "#f9f9f9", border: "0.5px solid #eee", borderRadius: 8, padding: "10px 16px", display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }} data-testid="workload-bar">
            <span style={{ fontSize: 13, fontWeight: 500 }} data-testid="workload-range-count">Total demos ({teamView === "day" ? "this day" : teamView === "month" ? "this month" : "this week"}): {rangeDemos.length}</span>
            <span style={{ fontSize: 13, color: (rangeUnassigned.length > 0) ? "#A32D2D" : "#888" }} data-testid="workload-unassigned">Unassigned: {rangeUnassigned.length}</span>
            <span style={{ fontSize: 13, color: "#888" }}>Avg demos/rep: {reps.length > 0 ? Math.round(rangeDemos.length / reps.length) : 0}</span>

            {/* View switcher (Day/Week/Month) + navigation */}
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }} data-testid="team-schedule-controls">
              <div style={{ display: "inline-flex", border: "0.5px solid #ddd", borderRadius: 8, overflow: "hidden", background: "#fff" }} data-testid="team-view-switcher">
                {["day", "week", "month"].map(v => (
                  <button key={v} onClick={() => setTeamView(v)} data-testid={`team-view-${v}`}
                    style={{ padding: "6px 14px", fontSize: 12, fontWeight: 500, border: "none", borderLeft: v !== "day" ? "0.5px solid #eee" : "none", background: teamView === v ? "#2563eb" : "#fff", color: teamView === v ? "#fff" : "#555", cursor: "pointer", fontFamily: "inherit", textTransform: "capitalize" }}>{v}</button>
                ))}
              </div>
              <button onClick={() => shiftAnchor(-1)} data-testid="team-nav-prev" aria-label="Previous range"
                style={{ width: 30, height: 30, borderRadius: 8, border: "0.5px solid #ddd", background: "#fff", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <ChevronLeft style={{ width: 14, height: 14, color: "#555" }} />
              </button>
              <button onClick={() => setTeamAnchor(new Date())} data-testid="team-nav-today"
                style={{ padding: "6px 12px", fontSize: 12, borderRadius: 8, border: "0.5px solid #ddd", background: "#fff", cursor: "pointer", fontFamily: "inherit", color: "#2563eb", fontWeight: 500 }}>Today</button>
              <button onClick={() => shiftAnchor(1)} data-testid="team-nav-next" aria-label="Next range"
                style={{ width: 30, height: 30, borderRadius: 8, border: "0.5px solid #ddd", background: "#fff", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <ChevronRight style={{ width: 14, height: 14, color: "#555" }} />
              </button>
              <span style={{ fontSize: 12, color: "#555", fontWeight: 500, minWidth: 140, textAlign: "right" }} data-testid="team-range-label">{rangeLabel}</span>
            </div>
          </div><div style={{ fontSize: 12, color: "#888", marginBottom: 12, marginTop: 4 }}>See all demo assignments across your team. Click any block for details or reassign.</div></div>
          <div style={{ display: "flex", gap: 20, fontSize: 11, color: "#888", marginBottom: 10, paddingLeft: 160 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}><div style={{ width: 12, height: 4, borderRadius: 2, background: "#1D9E75" }} /> Under capacity</span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}><div style={{ width: 12, height: 4, borderRadius: 2, background: "#BA7517" }} /> Near capacity</span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}><div style={{ width: 12, height: 4, borderRadius: 2, background: "#E24B4A" }} /> Over capacity</span>
          </div>
          <div style={{ border: "0.5px solid #eee", borderRadius: 8, overflow: "auto" }} data-testid="team-grid" data-testview={teamView}>
            <div style={{ display: "grid", gridTemplateColumns: dayColTemplate, background: "#f9f9f9", borderBottom: "0.5px solid #eee", minWidth: teamView === "month" ? 160 + 31 * 36 : undefined }}>
              <div style={{ padding: "10px 12px", fontSize: 11, fontWeight: 600, color: "#888" }}>Rep</div>
              {scheduleDates.map((d, i) => {
                const ds = scheduleDateStrs[i];
                const isToday = ds === today;
                return (
                  <div key={i} style={{ padding: teamView === "month" ? "8px 2px" : "10px 8px", textAlign: "center", background: isToday ? "#f0f7ff" : "transparent", borderLeft: teamView === "month" && i > 0 ? "0.5px dashed #f0f0f0" : "none" }} data-testid={`team-day-header-${ds}`}>
                    <div style={{ fontSize: teamView === "month" ? 9 : 11, color: "#888" }}>{d.toLocaleDateString("en-US", { weekday: teamView === "month" ? "narrow" : "short" })}</div>
                    <div style={{ fontSize: teamView === "month" ? 11 : 15, fontWeight: 500 }}>{d.getDate()}</div>
                  </div>
                );
              })}
            </div>
            {/* Unassigned row */}
            {rangeUnassigned.length > 0 && <div style={{ display: "grid", gridTemplateColumns: dayColTemplate, borderBottom: "0.5px solid #eee", minWidth: teamView === "month" ? 160 + 31 * 36 : undefined }}>
              <div style={{ padding: "10px 12px", borderRight: "0.5px solid #eee", background: "#FFF5F5", borderLeft: "3px solid #E24B4A", display: "flex", alignItems: "center", gap: 6 }}>
                <AlertTriangle style={{ width: 14, height: 14, color: "#A32D2D" }} /><span style={{ fontSize: 12, fontWeight: 500, color: "#A32D2D" }}>Unassigned</span>
              </div>
              {scheduleDates.map((d, i) => {
                const ds = scheduleDateStrs[i];
                const dayD = rangeUnassigned.filter(dm => dm.date === ds);
                if (teamView === "month") {
                  return (
                    <div key={i} style={{ borderRight: "0.5px solid #eee", padding: 2, minHeight: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {dayD.length > 0 && <button onClick={() => setShowOutcome(dayD[0])} title={`${dayD.length} unassigned on ${ds}`} style={{ background: "#FCEBEB", color: "#A32D2D", border: "none", borderRadius: 10, padding: "2px 6px", fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{dayD.length}</button>}
                    </div>
                  );
                }
                return (
                  <div key={i} style={{ borderRight: "0.5px solid #eee", padding: 6, minHeight: 70 }}>
                    {dayD.map(dm => <div key={dm.id} onClick={() => setShowOutcome(dm)} style={{ borderRadius: 4, padding: "4px 6px", marginBottom: 2, background: "#FCEBEB", color: "#A32D2D", cursor: "pointer" }}><div style={{ fontSize: 10, fontWeight: 500 }}>{fmt12(dm.time)}</div><div style={{ fontSize: 10 }}>{teamView === "day" ? (demoName(dm) + " — Assign →") : "Assign →"}</div></div>)}
                  </div>
                );
              })}
            </div>}
            {reps.map(rep => {
              const demosInRange = rep.demos.filter(dm => inRange(dm.date));
              const cnt = demosInRange.length;
              const capColor = cnt >= capThreshold ? "#E24B4A" : cnt >= capNear ? "#BA7517" : "#1D9E75";
              return (
                <div key={rep.name} style={{ display: "grid", gridTemplateColumns: dayColTemplate, borderBottom: "0.5px solid #eee", minWidth: teamView === "month" ? 160 + 31 * 36 : undefined }}>
                  <div style={{ padding: "10px 12px", borderRight: "0.5px solid #eee", display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#E6F1FB", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 600, color: "#185FA5" }}>{rep.name.split(" ").map(n => n[0]).join("")}</div>
                      <div><div style={{ fontSize: 12, fontWeight: 500 }}>{rep.name}</div><span style={{ fontSize: 10, color: "#888", background: "#f5f5f5", borderRadius: 10, padding: "1px 6px" }}>{cnt} demos</span></div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
                      <div style={{ width: 80, height: 4, borderRadius: 2, background: "#eee", overflow: "hidden" }}>
                        <div style={{ width: `${Math.min(cnt / capThreshold * 100, 100)}%`, height: "100%", background: capColor, borderRadius: 2 }} />
                      </div>
                      <span style={{ fontSize: 10, color: capColor }}>{cnt}/{capThreshold}</span>
                    </div>
                  </div>
                  {scheduleDates.map((d, i) => {
                    const ds = scheduleDateStrs[i];
                    const dayD = rep.demos.filter(dm => dm.date === ds);
                    const isToday = ds === today;
                    if (teamView === "month") {
                      // compact dot / count cell
                      if (dayD.length === 0) return <div key={i} style={{ borderRight: "0.5px solid #f0f0f0", minHeight: 40, background: isToday ? "#f0f7ff" : "#fff" }} />;
                      const sc = STATUS_COLORS[dayD[0].status] || STATUS_COLORS.confirmed;
                      return (
                        <div key={i} style={{ borderRight: "0.5px solid #f0f0f0", minHeight: 40, display: "flex", alignItems: "center", justifyContent: "center", background: isToday ? "#f0f7ff" : "#fff" }}>
                          <button onClick={() => setShowOutcome(dayD[0])} title={`${dayD.length} demo(s) on ${ds}`} style={{ background: sc.bg, color: sc.color, border: "none", borderRadius: 10, padding: "2px 6px", fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", lineHeight: 1.3 }}>{dayD.length}</button>
                        </div>
                      );
                    }
                    // day or week layout
                    const shown = teamView === "day" ? dayD : dayD.slice(0, 2);
                    return (
                      <div key={i} style={{ borderRight: "0.5px solid #eee", padding: 6, minHeight: teamView === "day" ? 100 : 70, background: isToday ? "#f0f7ff" : "#fff" }}>
                        {shown.map(dm => {
                          const sc = STATUS_COLORS[dm.status] || STATUS_COLORS.confirmed;
                          return (
                            <div key={dm.id} onClick={() => setShowOutcome(dm)} style={{ borderRadius: 4, padding: "4px 6px", marginBottom: 2, background: sc.bg, color: sc.color, cursor: "pointer" }}>
                              <div style={{ fontSize: 10, fontWeight: 500 }}>{fmt12(dm.time)}</div>
                              <div style={{ fontSize: 10, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{teamView === "day" ? demoName(dm) : trunc(demoName(dm), 10)}</div>
                            </div>
                          );
                        })}
                        {teamView === "week" && dayD.length > 2 && <div style={{ fontSize: 9, color: "#888", background: "#f5f5f5", borderRadius: 4, padding: "1px 4px" }}>+{dayD.length - 2} more</div>}
                        {teamView === "day" && dayD.length === 0 && <div style={{ fontSize: 10, color: "#bbb", padding: "4px 2px" }}>—</div>}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {reps.length === 0 && rangeUnassigned.length === 0 && <div style={{ padding: 32, textAlign: "center", color: "#888", fontSize: 13 }}>No reps with scheduled demos.</div>}
          </div>
        </>)}

        {/* ══ ANALYTICS ══ */}
        {activeTab === "analytics" && canSeeAnalytics && (<>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }} data-testid="analytics-metrics">
            {[{ label: "Total Demos Booked", value: activeDemos.length },{ label: "Show Rate", value: `${stats?.show_rate || 0}%` },{ label: "Demo to Proposal", value: `${completedDemos.length > 0 ? Math.round(demoToProposal / completedDemos.length * 100) : 0}%` },{ label: "Demo to Close", value: `${completedDemos.length > 0 ? Math.round(demoToClose / completedDemos.length * 100) : 0}%` }].map((m, i) => (<div key={i} style={{ background: "#fff", border: "0.5px solid #eee", borderRadius: 10, padding: "16px 20px" }}><div style={{ fontSize: 12, textTransform: "uppercase", color: "#888" }}>{m.label}</div><div style={{ fontSize: 28, fontWeight: 500, marginTop: 4 }}>{m.value}</div></div>))}
          </div>
          <div style={{ display: "flex", gap: 14, marginBottom: 14 }}>
            <div style={{ flex: 1, border: "0.5px solid #eee", borderRadius: 8, padding: 16, background: "#fff" }}><div style={{ ...SL, marginBottom: 12 }}>Outcomes Breakdown</div>
              {outcomeData.length > 0 ? <ResponsiveContainer width="100%" height={220}><BarChart data={outcomeData} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis type="number" tick={{ fontSize: 11 }} /><YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="count" fill="#2563eb" radius={[0, 4, 4, 0]} /></BarChart></ResponsiveContainer> : <div style={{ fontSize: 12, color: "#888", textAlign: "center", padding: 40 }}>No outcome data yet</div>}
            </div>
            <div style={{ flex: 1, border: "0.5px solid #eee", borderRadius: 8, padding: 16, background: "#fff" }}><div style={{ ...SL, marginBottom: 12 }}>Demos by Rep</div>
              {reps.length > 0 ? <ResponsiveContainer width="100%" height={220}><BarChart data={reps.map(r => ({ name: r.name.split(" ")[0], demos: r.demos.length }))} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis type="number" tick={{ fontSize: 11 }} /><YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="demos" fill="#1D9E75" radius={[0, 4, 4, 0]} /></BarChart></ResponsiveContainer> : <div style={{ fontSize: 12, color: "#888", textAlign: "center", padding: 40 }}>No rep data yet</div>}
            </div>
          </div>
          <div style={{ ...SL, marginBottom: 10 }}>Rep performance breakdown</div>
          <div style={{ border: "0.5px solid #eee", borderRadius: 8, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "160px repeat(5, 1fr)", padding: "10px 16px", background: "#f7f7f7", borderBottom: "0.5px solid #eee", fontSize: 9, fontWeight: 700, color: "#888", textTransform: "uppercase" }}><span>Rep</span><span>Booked</span><span>Completed</span><span>Show Rate</span><span>No-Show</span><span>Reschedule</span></div>
            {reps.map(r => { const c = r.demos.filter(d => d.status === "completed").length; const ns = r.demos.filter(d => d.status === "no_show").length; const rs = r.demos.filter(d => d.status === "rescheduled").length; const t = r.demos.length; return (
              <div key={r.name} style={{ display: "grid", gridTemplateColumns: "160px repeat(5, 1fr)", padding: "10px 16px", borderBottom: "0.5px solid #eee", fontSize: 12 }}>
                <span style={{ fontWeight: 500 }}>{r.name}</span><span>{t}</span><span>{c}</span>
                <span style={{ color: t > 0 && (c / t * 100) < 70 ? "#A32D2D" : "#3B6D11" }}>{t > 0 ? Math.round(c / t * 100) : 0}%</span>
                <span style={{ color: ns / Math.max(t, 1) * 100 > 15 ? "#A32D2D" : "#888" }}>{t > 0 ? Math.round(ns / t * 100) : 0}%</span>
                <span>{t > 0 ? Math.round(rs / t * 100) : 0}%</span>
              </div>); })}
          </div>
        </>)}

        {/* ══ LIVE MEETING ══ */}
        {activeTab === "live-meeting" && (() => {
          const todayStr = new Date().toISOString().split("T")[0];
          const todayDemos = demos.filter(d => d.date === todayStr && (d.status === "confirmed" || d.status === "scheduled")).sort((a, b) => (a.time || "").localeCompare(b.time || ""));
          const nextDemo = liveMeetingDemo || todayDemos[0] || null;
          const rcMeetingId = nextDemo?.meeting_link?.match(/\/j\/(\d+)/)?.[1] || nextDemo?.meeting_id || "123456789";
          const rcLink = nextDemo?.meeting_link || `https://meetings.ringcentral.com/j/${rcMeetingId}`;
          return (
            <div>
              {/* Meeting Header */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 14 }}>
                <div>
                  {nextDemo ? (<>
                    <div className="card" style={{ padding: "18px 22px", marginBottom: 12, borderLeft: meetingActive ? "3px solid #ff3b30" : "3px solid #2563eb" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 600, color: meetingActive ? "#ff3b30" : "#888", textTransform: "uppercase" }}>{meetingActive ? "Meeting in progress" : "Next demo"}</div>
                          <div style={{ fontSize: 18, fontWeight: 600, marginTop: 2 }}>{demoName(nextDemo)}</div>
                          <div style={{ fontSize: 12, color: "#888" }}>{fmtDate(nextDemo.date)} · {fmt12(nextDemo.time)} · {nextDemo.product || "Product demo"}</div>
                        </div>
                        {!meetingActive ? (
                          <button onClick={() => setMeetingActive(true)} className="btn btn-primary" style={{ fontSize: 13, padding: "10px 24px" }} data-testid="join-meeting-btn"><Phone style={{ width: 14, height: 14 }} /> Join Meeting</button>
                        ) : (
                          <button onClick={() => { setMeetingActive(false); setShowOutcome(nextDemo); toast.success("Meeting ended — log your disposition"); }} style={{ background: "#ff3b30", color: "#fff", border: "none", borderRadius: 8, padding: "10px 24px", fontSize: 13, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 }} data-testid="end-meeting-btn"><PhoneOff style={{ width: 14, height: 14 }} /> End & Log Outcome</button>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 12, fontSize: 11, color: "#888" }}>
                        <span>Host: {nextDemo.host_rep_name || "You"}</span>
                        <span>Contact: {demoContact(nextDemo)}</span>
                        <span>Meeting ID: {rcMeetingId}</span>
                      </div>
                    </div>

                    {/* RingCentral Embedded Meeting */}
                    <div className="card" style={{ padding: 0, overflow: "hidden", height: meetingActive ? 480 : 200, transition: "height 0.3s", position: "relative" }} data-testid="rc-meeting-embed">
                      {meetingActive ? (
                        <iframe src="https://apps.ringcentral.com/integration/ringcentral-embeddable/latest/app.html" title="RingCentral Meeting" style={{ width: "100%", height: "100%", border: "none" }} allow="camera;microphone;display-capture" />
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", background: "#f9f9f9" }}>
                          <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#ff6c3715", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
                            <Phone style={{ width: 20, height: 20, color: "#ff6c37" }} />
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 500, color: "#888" }}>Click "Join Meeting" to launch RingCentral</div>
                          <div style={{ fontSize: 12, color: "#aaa", marginTop: 4 }}>Embedded RingCentral meeting will open here</div>
                          <a href={rcLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: "#2563eb", marginTop: 8, display: "flex", alignItems: "center", gap: 4 }}>Open in new tab instead &rarr;</a>
                        </div>
                      )}
                    </div>
                  </>) : (
                    <div style={{ textAlign: "center", padding: 60, color: "#888" }}>
                      <Phone style={{ width: 32, height: 32, color: "#ddd", margin: "0 auto 12px" }} />
                      <div style={{ fontSize: 14, fontWeight: 500 }}>No demos scheduled for today</div>
                      <div style={{ fontSize: 12, color: "#aaa", marginTop: 4 }}>Upcoming demos will appear here when they're on today's calendar.</div>
                    </div>
                  )}
                </div>

                {/* Right Panel — AI prep / transcript */}
                <div>
                  {nextDemo && (<>
                    <div style={{ background: "#0F2942", borderRadius: 10, padding: "14px 16px", marginBottom: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                        <Sparkles style={{ width: 12, height: 12, color: "#93C5FD" }} />
                        <span style={{ fontSize: 12, fontWeight: 500, color: "#fff" }}>{meetingActive ? "AI Meeting Assistant" : "Demo Prep Brief"}</span>
                      </div>
                      {!meetingActive ? (<>
                        <div style={{ fontSize: 11, color: "#CBD5E0", lineHeight: 1.7, marginBottom: 6 }}><strong style={{ color: "#93C5FD" }}>Lead:</strong> {demoName(nextDemo)} — {nextDemo.specialty || "Healthcare"} practice, {nextDemo.providers || "multiple"} providers</div>
                        <div style={{ fontSize: 11, color: "#CBD5E0", lineHeight: 1.7, marginBottom: 6 }}><strong style={{ color: "#FFB84D" }}>Focus areas:</strong> {nextDemo.product || "AI-powered EHR"} — emphasize workflow automation and billing integration</div>
                        <div style={{ fontSize: 11, color: "#CBD5E0", lineHeight: 1.7 }}><strong style={{ color: "#34c759" }}>Prep:</strong> Review their current system pain points. They've had {nextDemo.attempts || 0} prior touchpoints. {getLeadScore(nextDemo) > 70 ? "High intent — push for close." : "Discovery phase — build rapport."}</div>
                      </>) : (<>
                        <div style={{ fontSize: 11, color: "#CBD5E0", lineHeight: 1.7, marginBottom: 6 }}><strong style={{ color: "#34c759" }}>Live analysis:</strong> Meeting in progress. AI is analyzing conversation for key topics, sentiment, and action items.</div>
                        <div style={{ fontSize: 11, color: "#CBD5E0", lineHeight: 1.7, marginBottom: 6 }}><strong style={{ color: "#93C5FD" }}>Detected topics:</strong> Data migration, staff training, pricing</div>
                        <div style={{ fontSize: 11, color: "#CBD5E0", lineHeight: 1.7 }}><strong style={{ color: "#FFB84D" }}>Sentiment:</strong> Prospect appears engaged and asking detailed questions</div>
                      </>)}
                    </div>
                    {meetingActive && (
                      <div className="card" style={{ padding: "12px 14px" }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "#888", marginBottom: 6 }}>Live transcript preview</div>
                        <div style={{ fontSize: 11, color: "#888", lineHeight: 1.7, maxHeight: 200, overflow: "auto", background: "#f9f9f9", borderRadius: 6, padding: 8 }}>
                          <div style={{ marginBottom: 6 }}><span style={{ color: "#2563eb", fontWeight: 500 }}>You:</span> Thanks for joining today. Let me walk you through our EHR platform...</div>
                          <div style={{ marginBottom: 6 }}><span style={{ color: "#1D9E75", fontWeight: 500 }}>{demoContact(nextDemo) || "Prospect"}:</span> Yes, we've been looking at options for a while now...</div>
                          <div><span style={{ color: "#888", fontStyle: "italic" }}>Transcription updates live during meeting...</span></div>
                        </div>
                      </div>
                    )}
                    {!meetingActive && (
                      <div className="card" style={{ padding: "12px 14px" }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "#888", marginBottom: 8 }}>Today's demos</div>
                        {todayDemos.length === 0 && <div style={{ fontSize: 12, color: "#aaa" }}>No more demos today</div>}
                        {todayDemos.map(d => (
                          <div key={d.id} onClick={() => setLiveMeetingDemo(d)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "0.5px solid #eee", cursor: "pointer" }}>
                            <div style={{ width: 3, borderRadius: 2, height: 24, background: d.id === nextDemo?.id ? "#2563eb" : "#eee" }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 12, fontWeight: 500 }}>{demoName(d)}</div>
                              <div style={{ fontSize: 10, color: "#888" }}>{fmt12(d.time)} · {d.status}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>)}
                </div>
              </div>
            </div>
          );
        })()}

        {/* ══ SETTINGS ══ */}
        {activeTab === "settings" && settings && (<>
          {settingsDirty && <div style={{ background: "#FAEEDA", borderBottom: "1px solid #FAC775", padding: "6px 16px", fontSize: 12, color: "#854F0B", marginBottom: 12, borderRadius: 8 }}>You have unpublished changes — preview or publish when ready.</div>}
          {canSeeFullSettings ? (<>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
              <div><div style={{ fontSize: 18, fontWeight: 500 }}>Demo Booking Site Settings</div><div style={{ fontSize: 13, color: "#888" }}>Configure your public demo booking page. Preview changes before publishing.</div></div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => window.open("/book-demo", "_blank")} style={{ border: "0.5px solid #2563eb", color: "#2563eb", background: "#fff", borderRadius: 8, padding: "7px 16px", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}>Preview site</button>
                <div style={{ position: "relative" }}>
                  <button onClick={publishSettings} disabled={!settingsDirty} style={{ background: settingsDirty ? "#2563eb" : "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "7px 16px", fontSize: 13, fontWeight: 500, cursor: settingsDirty ? "pointer" : "not-allowed", opacity: settingsDirty ? 1 : 0.4, fontFamily: "inherit" }} data-testid="publish-btn">Publish changes</button>
                  {settingsDirty && <div style={{ position: "absolute", top: -3, right: -3, width: 8, height: 8, borderRadius: "50%", background: "#BA7517" }} />}
                </div>
              </div>
            </div>
            <SettingsSection title="Integrations — Outlook calendar sync (Phase A + C)" subtitle="When ON, every confirmed demo booking is mirrored onto the assigned agent's Outlook calendar via Microsoft Graph (Phase A — outbound). Per-rep inbound sync (Outlook → CRM) is managed via the Subscribe button below (Phase C). Requires each agent to have connected their M365 account in Settings → Integrations." defaultOpen>
              <div data-testid="integrations-calendar-sync-row" style={{ marginTop: 12, padding: 12, background: "#F8FAFC", border: "0.5px solid #E2E8F0", borderRadius: 8, display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div
                  data-testid="toggle-calendar-sync"
                  onClick={async () => {
                    const next = !settings.calendar_sync_enabled;
                    try {
                      await axios.patch(`${API}/demo-booking/admin/settings`, { calendar_sync_enabled: next }, { headers: { Authorization: `Bearer ${token}` } });
                      updateSettings("calendar_sync_enabled", next);
                      toast.success(next ? "Outlook calendar sync ON — new demos will appear on agents' calendars." : "Outlook calendar sync OFF.");
                    } catch { toast.error("Couldn't update setting"); }
                  }}
                  style={{ flexShrink: 0, marginTop: 2, width: 36, height: 20, borderRadius: 10, background: settings.calendar_sync_enabled ? "#1D9E75" : "#cbd5e1", position: "relative", cursor: "pointer", transition: "background 120ms ease" }}
                >
                  <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: settings.calendar_sync_enabled ? 18 : 2, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>Sync demo bookings to Outlook calendar</div>
                  <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 3, lineHeight: 1.5 }}>
                    When ON, each newly-booked, rescheduled, or cancelled demo is mirrored onto the organizer's Outlook calendar through Microsoft Graph. Agents see the same meeting in their inbox client without any manual entry.
                  </div>
                  <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 6, fontStyle: "italic" }}>
                    Status: <strong style={{ color: settings.calendar_sync_enabled ? "#1D9E75" : "#94a3b8" }}>{settings.calendar_sync_enabled ? "ON" : "OFF"}</strong>
                    {" · "}
                    Failures are silently logged — they never block a CRM booking write.
                  </div>
                </div>
              </div>
              <OutlookInboundSubscribeButton />
              {/* Iter 175.4 — admin one-click bulk-subscribe. Surfaces
                  the /api/admin/integrations/m365/calendar/subscribe-all
                  endpoint so an admin can re-enable calendar push for
                  the entire team after a deploy / env-var change
                  without using curl. */}
              <OutlookAdminBulkSubscribeRow />
            </SettingsSection>
            <SettingsSection title="Branding & Page Content" subtitle="Customize text and appearance of your public booking page" defaultOpen>
              <div style={{ marginTop: 12 }}>
                <label style={{ ...SL, display: "block", marginBottom: 4 }}>Page headline</label>
                <input value={settings.page_title || ""} onChange={e => updateSettings("page_title", e.target.value)} style={{ width: "100%", padding: "8px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 13, fontFamily: "inherit" }} />
                <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>Shown at the top of your public booking page</div>
              </div>
              <div style={{ marginTop: 12 }}>
                <label style={{ ...SL, display: "block", marginBottom: 4 }}>Page subheadline</label>
                <textarea value={settings.page_subtitle || ""} onChange={e => updateSettings("page_subtitle", e.target.value)} rows={2} style={{ width: "100%", padding: "8px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit", resize: "none" }} />
              </div>
              <div style={{ marginTop: 12 }}>
                <label style={{ ...SL, display: "block", marginBottom: 4 }}>Primary brand color</label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 6, border: "0.5px solid #ddd", background: settings.brand_color || "#2563eb" }} />
                  <input value={settings.brand_color || "#2563eb"} onChange={e => updateSettings("brand_color", e.target.value)} style={{ width: 80, padding: "6px 8px", border: "0.5px solid #ddd", borderRadius: 6, fontSize: 12, fontFamily: "inherit" }} />
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <label style={{ ...SL, display: "block", marginBottom: 4 }}>Confirmation message</label>
                <textarea value={settings.confirmation_message || ""} onChange={e => updateSettings("confirmation_message", e.target.value)} rows={3} style={{ width: "100%", padding: "8px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit", resize: "none" }} />
              </div>
            </SettingsSection>
            <SettingsSection title="Hours of Operation" subtitle="Set when demos can be booked on your public page">
              <div style={{ marginTop: 12 }}>
                <label style={{ ...SL, display: "block", marginBottom: 8 }}>Business hours</label>
                {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map(day => {
                  const h = settings.hours?.[day] || { enabled: false, from: "08:00", to: "18:00" };
                  return (<div key={day} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: "0.5px solid #eee", opacity: h.enabled ? 1 : 0.5 }}>
                    <div style={{ width: 120, display: "flex", alignItems: "center", gap: 8 }}>
                      <div onClick={() => { const newH = { ...settings.hours, [day]: { ...h, enabled: !h.enabled } }; updateSettings("hours", newH); }} style={{ width: 32, height: 18, borderRadius: 9, background: h.enabled ? "#1D9E75" : "#ddd", position: "relative", cursor: "pointer" }}><div style={{ width: 14, height: 14, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: h.enabled ? 16 : 2, transition: "left 0.2s" }} /></div>
                      <span style={{ fontSize: 13 }}>{day}</span>
                    </div>
                    {h.enabled && <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 11, color: "#888" }}>From:</span>
                      <input type="time" value={h.from} onChange={e => { const newH = { ...settings.hours, [day]: { ...h, from: e.target.value } }; updateSettings("hours", newH); }} style={{ padding: "4px 8px", border: "0.5px solid #ddd", borderRadius: 6, fontSize: 12, fontFamily: "inherit" }} />
                      <span style={{ fontSize: 11, color: "#888" }}>To:</span>
                      <input type="time" value={h.to} onChange={e => { const newH = { ...settings.hours, [day]: { ...h, to: e.target.value } }; updateSettings("hours", newH); }} style={{ padding: "4px 8px", border: "0.5px solid #ddd", borderRadius: 6, fontSize: 12, fontFamily: "inherit" }} />
                    </div>}
                  </div>);
                })}
              </div>
              <div style={{ marginTop: 12 }}><label style={{ ...SL, display: "block", marginBottom: 4 }}>Buffer between demos</label>
                <select value={settings.buffer_minutes || 30} onChange={e => updateSettings("buffer_minutes", parseInt(e.target.value))} style={{ padding: "6px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit" }}>
                  {[0,15,30,45,60].map(m => <option key={m} value={m}>{m} min</option>)}
                </select><div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>Prevent back-to-back bookings</div></div>
              <div style={{ marginTop: 12 }}><label style={{ ...SL, display: "block", marginBottom: 4 }}>Minimum advance notice</label>
                <select value={settings.min_advance_hours || 24} onChange={e => updateSettings("min_advance_hours", parseInt(e.target.value))} style={{ padding: "6px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit" }}>
                  {[1,2,4,24,48].map(h => <option key={h} value={h}>{h} hour{h > 1 ? "s" : ""}</option>)}
                </select></div>
            </SettingsSection>
            <SettingsSection title="Calendar Blocking" subtitle="Block specific dates or date ranges from the public booking page">
              <div style={{ marginTop: 12 }}>
                <CalendarBlockingUI settings={settings} token={token} onUpdated={(blocks) => setSettings(prev => ({ ...prev, blocked_dates: blocks }))} />
              </div>
            </SettingsSection>
            <SettingsSection title="Virtual Meeting Settings (RingCentral)" subtitle="Configure RingCentral meeting IDs and defaults">
              <div style={{ background: "#E6F1FB", border: "0.5px solid #B5D4F4", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#185FA5", marginTop: 12, marginBottom: 14 }}>All demos conducted via RingCentral. The correct meeting link is included automatically in booking confirmations.</div>
              <div style={{ marginBottom: 12 }}><label style={{ ...SL, display: "block", marginBottom: 4 }}>Default meeting ID</label><input value={settings.default_meeting_id || ""} onChange={e => updateSettings("default_meeting_id", e.target.value)} placeholder="e.g. 123 456 7890" style={{ width: "100%", padding: "8px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 13, fontFamily: "inherit" }} /><div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>Used when no rep-specific ID is assigned</div></div>
              <div style={{ marginBottom: 12 }}><label style={{ ...SL, display: "block", marginBottom: 4 }}>Default meeting password (optional)</label><input type="password" value={settings.default_meeting_password || ""} onChange={e => updateSettings("default_meeting_password", e.target.value)} placeholder="Leave blank if none" style={{ width: "100%", padding: "8px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit" }} /></div>
              <div><label style={{ ...SL, display: "block", marginBottom: 4 }}>Meeting link format</label><input value={settings.meeting_link_format || ""} onChange={e => updateSettings("meeting_link_format", e.target.value)} style={{ width: "100%", padding: "8px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit" }} /><div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{"{meeting_id}"} replaced automatically</div></div>
            </SettingsSection>
            <SettingsSection title="Demo Products & Solutions" subtitle="Configure which products leads can select">
              <div style={{ marginTop: 12 }}>{(settings.products || []).map((p, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: "0.5px solid #eee" }}>
                  <div onClick={() => { const prods = [...settings.products]; prods[i] = { ...prods[i], enabled: !prods[i].enabled }; updateSettings("products", prods); }} style={{ width: 28, height: 16, borderRadius: 8, background: p.enabled ? "#1D9E75" : "#ddd", position: "relative", cursor: "pointer", flexShrink: 0 }}><div style={{ width: 12, height: 12, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: p.enabled ? 14 : 2, transition: "left 0.2s" }} /></div>
                  <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 500 }}>{p.name}</div><div style={{ fontSize: 11, color: "#888" }}>{p.description}</div></div>
                  <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 10, background: "#f5f5f5", color: "#888" }}>{p.category}</span>
                </div>))}</div>
            </SettingsSection>
            <SettingsSection title="Booking Form Fields" subtitle="Configure which fields appear on the public booking form" data-testid="booking-form-fields-section">
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, color: "#888", marginBottom: 10 }}>Locked fields are always visible and required. Toggle visibility and required status for other fields.</div>
                {[
                  { id: "first_name", label: "First name", locked: true, visible: true, required: true },
                  { id: "last_name", label: "Last name", locked: true, visible: true, required: true },
                  { id: "email", label: "Email", locked: true, visible: true, required: true },
                  { id: "phone", label: "Phone", locked: false, visible: true, required: false },
                  { id: "company", label: "Practice name", locked: false, visible: true, required: false },
                  { id: "job_title", label: "Job title", locked: false, visible: false, required: false },
                  { id: "specialty", label: "Specialty", locked: false, visible: true, required: false },
                  { id: "providers", label: "Number of providers", locked: false, visible: true, required: false },
                  { id: "current_ehr", label: "Current EHR system", locked: false, visible: false, required: false },
                  { id: "referral_source", label: "How did you hear about us?", locked: false, visible: false, required: false },
                  { id: "notes", label: "Additional notes", locked: false, visible: true, required: false },
                ].map(field => (
                  <div key={field.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: "0.5px solid #eee" }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{field.label}</span>
                      {field.locked && <span style={{ marginLeft: 6, fontSize: 9, padding: "1px 5px", borderRadius: 4, background: "#f5f5f5", color: "#888" }}>Locked</span>}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ fontSize: 10, color: "#888" }}>Visible</span>
                        <div style={{ width: 28, height: 16, borderRadius: 8, background: field.visible ? "#1D9E75" : "#ddd", position: "relative", cursor: field.locked ? "default" : "pointer", opacity: field.locked ? 0.5 : 1 }}>
                          <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: field.visible ? 14 : 2 }} />
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ fontSize: 10, color: "#888" }}>Required</span>
                        <div style={{ width: 28, height: 16, borderRadius: 8, background: field.required ? "#2563eb" : "#ddd", position: "relative", cursor: field.locked ? "default" : "pointer", opacity: field.locked ? 0.5 : 1 }}>
                          <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: field.required ? 14 : 2 }} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </SettingsSection>
            <SettingsSection title="Notifications & Confirmations" subtitle="Control automated emails and alerts">
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>Lead notifications</div>
                {[["booking_confirmation","Booking confirmation email","Sent immediately after booking"],["reminder_24h","24-hour reminder","Remind lead 24 hours before"],["reminder_1h","1-hour reminder","Remind lead 1 hour before"],["post_demo_followup","Post-demo follow-up","Sent 2 hours after demo time"],["reschedule_confirmation","Reschedule confirmation","When lead reschedules"],["cancel_confirmation","Cancellation confirmation","When lead cancels"]].map(([key, label, desc]) => (
                  <div key={key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}>
                    <div onClick={() => { const n = { ...settings.notifications, [key]: !settings.notifications?.[key] }; updateSettings("notifications", n); }} style={{ width: 32, height: 18, borderRadius: 9, background: settings.notifications?.[key] ? "#1D9E75" : "#ddd", position: "relative", cursor: "pointer", flexShrink: 0 }}><div style={{ width: 14, height: 14, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: settings.notifications?.[key] ? 16 : 2, transition: "left 0.2s" }} /></div>
                    <div><div style={{ fontSize: 13 }}>{label}</div><div style={{ fontSize: 11, color: "#888" }}>{desc}</div></div>
                  </div>
                ))}
                <div style={{ fontSize: 12, fontWeight: 500, marginTop: 14, marginBottom: 8 }}>Rep / internal notifications</div>
                {[["rep_new_booking","New booking alert","Notify rep when a demo is booked"],["rep_reschedule","Reschedule alert","Notify rep when lead reschedules"],["rep_cancellation","Cancellation alert","Notify rep when lead cancels"],["rep_no_show","No-show alert","Alert rep 15 min after if no join"],["rep_daily_summary","Daily booking summary","Morning summary of upcoming demos"]].map(([key, label, desc]) => (
                  <div key={key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}>
                    <div onClick={() => { const n = { ...settings.notifications, [key]: !settings.notifications?.[key] }; updateSettings("notifications", n); }} style={{ width: 32, height: 18, borderRadius: 9, background: settings.notifications?.[key] ? "#1D9E75" : "#ddd", position: "relative", cursor: "pointer", flexShrink: 0 }}><div style={{ width: 14, height: 14, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: settings.notifications?.[key] ? 16 : 2, transition: "left 0.2s" }} /></div>
                    <div><div style={{ fontSize: 13 }}>{label}</div><div style={{ fontSize: 11, color: "#888" }}>{desc}</div></div>
                  </div>
                ))}
              </div>
            </SettingsSection>
            <SettingsSection title="Lead Routing & Assignment" subtitle="Control how incoming demos are assigned to reps">
              <div style={{ marginTop: 12 }}><label style={{ ...SL, display: "block", marginBottom: 8 }}>Demo assignment method</label>
                <div style={{ display: "flex", gap: 6 }}>
                  {[{ id: "round_robin", label: "Round robin" },{ id: "manual", label: "Manual" },{ id: "by_specialty", label: "By specialty" },{ id: "by_territory", label: "By territory" }].map(m => (
                    <button key={m.id} onClick={() => updateSettings("assignment_method", m.id)} style={{ padding: "6px 14px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontFamily: "inherit", border: (settings.assignment_method || "round_robin") === m.id ? "none" : "0.5px solid #ddd", background: (settings.assignment_method || "round_robin") === m.id ? "#2563eb" : "#fff", color: (settings.assignment_method || "round_robin") === m.id ? "#fff" : "#888" }}>{m.label}</button>
                  ))}
                </div>
              </div>
            </SettingsSection>
          </>) : (
            /* Sales Rep limited settings view */
            <div>
              <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 4 }}>My Demo Settings</div>
              <div style={{ fontSize: 13, color: "#888", marginBottom: 12 }}>Manage your personal availability and meeting settings.</div>
              <div style={{ background: "#E6F1FB", border: "0.5px solid #B5D4F4", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#185FA5", marginBottom: 16 }}>Contact your admin to change site-wide settings.</div>
              <SettingsSection title="My Availability" subtitle="Set your personal demo availability" defaultOpen>
                <div style={{ marginTop: 12, fontSize: 12, color: "#888" }}>Your availability settings will be saved automatically.</div>
              </SettingsSection>
              <SettingsSection title="My RingCentral Meeting ID" subtitle="Your personal meeting settings" defaultOpen>
                <div style={{ marginTop: 12 }}><label style={{ ...SL, display: "block", marginBottom: 4 }}>Meeting ID</label><input placeholder="Your RingCentral meeting ID" style={{ width: "100%", padding: "8px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 13, fontFamily: "inherit" }} /></div>
                <div style={{ marginTop: 12 }}><label style={{ ...SL, display: "block", marginBottom: 4 }}>Password (optional)</label><input type="password" placeholder="Leave blank if none" style={{ width: "100%", padding: "8px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit" }} /></div>
              </SettingsSection>
            </div>
          )}
        </>)}

      </div>

      {/* Modals */}
      {showSchedule && (isClient
        ? <ClientDemoBookingModal onClose={() => setShowSchedule(false)} onBooked={fetchAll} />
        : <ScheduleDemoUnified token={token} onClose={() => { setShowSchedule(false); setPrefillLead(null); }} onScheduled={fetchAll} prefillLead={prefillLead} />)}
      {showReschedule && (
        <RescheduleDemoModal
          demo={showReschedule}
          token={token}
          onClose={() => setShowReschedule(null)}
          onRescheduled={fetchAll}
        />
      )}
      {showOutcome && demoClientId(showOutcome) && (
        <EhiDemoOutcomeModal demo={showOutcome} token={token} onClose={() => setShowOutcome(null)} onSaved={(st) => { onOutcomeSaved(showOutcome.id, st); setShowOutcome(null); }} />
      )}
      {showOutcome && !demoClientId(showOutcome) && isClient && (
        <DemoOutcomeModal demo={showOutcome} token={token} onClose={() => setShowOutcome(null)} onSaved={() => { onOutcomeSaved(showOutcome.id); setShowOutcome(null); }} />
      )}
      {showOutcome && !demoClientId(showOutcome) && !isClient && (
        <DispositionModal
          lead={{
            id: showOutcome.lead_id,
            clinic: showOutcome.company || showOutcome.contact_name || "Demo lead",
            company: showOutcome.company,
            stage: "demo",
            subStatus: "",
          }}
          timer={0}
          notes={[]}
          contactsCalled={[]}
          onClose={() => setShowOutcome(null)}
          onSave={async (p) => {
            const leadId = showOutcome.lead_id;
            if (!leadId) { toast.error("This demo isn't linked to a lead — can't log a disposition."); return false; }
            try {
              // (a) unified disposition record
              await axios.post(`${API}/dispositions`, {
                lead_id: leadId,
                outcome: p.outcome, callType: p.callType, sentiment: p.sentiment,
                stage: p.stage, substatus: p.substatus, nextStep: p.nextStep,
                decisionMakerReached: p.decisionMakerReached, budgetConfirmed: p.budgetConfirmed,
                recordingUrl: p.recordingUrl, notes: p.notes, followup: p.followup,
                advance: p.advance, completed: p.completed !== false,
                contactDispositions: p.contactDispositions, leadEdits: p.leadEdits,
                aiApplied: p.aiApplied, attachments: p.attachments, followupTasks: p.followupTasks,
                productTags: p.productTags, duration_seconds: 0,
                demoBookingId: showOutcome.id,
              }, { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 });
              // (b) if a "Demo Completed" tier sub-status was chosen, mark the
              // booking complete so it leaves the queue + cascades metrics.
              const demoOutcome = SUBSTATUS_TO_DEMO_OUTCOME[p.substatus];
              if (demoOutcome) {
                await axios.post(`${API}/demo-bookings/${showOutcome.id}/complete`, {
                  outcome: demoOutcome,
                  outcome_notes: typeof p.notes === "string" ? p.notes : "",
                  next_step: p.nextStep || undefined,
                }, { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 });
              }
              toast.success(demoOutcome ? "Outcome logged & demo marked complete" : "Disposition saved");
              onOutcomeSaved(showOutcome.id);
              setShowOutcome(null);
              return true;
            } catch (err) {
              const status = err?.response?.status;
              const detail = err?.response?.data?.detail;
              toast.error(`Save failed (${status || "network"})${typeof detail === "string" ? ` · ${detail}` : ""} — please retry.`, { duration: 10000 });
              return false;
            }
          }}
        />
      )}
      {/* Shared Lead Filter sidebar — state lives in localStorage and
          mirrors the /leads + /contacts panel. Filters carry over across
          all 3 pages. */}
      {showLeadFilters && (
        <LeadFilterPanel
          filters={leadFilters}
          onChange={setLeadFilters}
          extra={leadExtra}
          onChangeExtra={setLeadExtra}
          onClose={() => setShowLeadFilters(false)}
        />
      )}

      {/* Demo Detail Lightbox */}
      {demoDetail && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setDemoDetail(null)}>
          <div style={{ background: "#fff", borderRadius: 14, width: 580, maxHeight: "85vh", overflow: "auto", border: "0.5px solid #eee" }} onClick={e => e.stopPropagation()} data-testid="demo-detail-lightbox">
            <div style={{ padding: "16px 20px", borderBottom: "0.5px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 15, fontWeight: 500 }}>Demo details</span>
              <button onClick={() => setDemoDetail(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#888" }}>&times;</button>
            </div>
            {/* AI Summary */}
            <div style={{ background: "#0F2942", margin: "16px 20px 0", borderRadius: 10, padding: "14px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                <Sparkles style={{ width: 12, height: 12, color: "#93C5FD" }} />
                <span style={{ fontSize: 12, fontWeight: 500, color: "#fff" }}>AI Summary</span>
              </div>
              <div style={{ fontSize: 12, color: "#CBD5E0", lineHeight: 1.7 }}>
                {demoName(demoDetail)} from {demoDetail.company || demoDetail.lead_title || "practice"} — {demoDetail.specialty || "healthcare"} specialty.
                {getLeadScore(demoDetail) > 70 ? " High-intent lead with strong AI score." : getLeadScore(demoDetail) > 40 ? " Medium-intent — discovery phase." : " Early stage contact."}
                {" "}Demo focuses on {demoDetail.product || "AI-powered EHR"}, scheduled for {fmtDate(demoDetail.date)} at {fmt12(demoDetail.time)}.
                {demoDetail.outcome ? ` Outcome: ${demoDetail.outcome.replace(/_/g, " ")}.` : " Outcome pending."}
              </div>
            </div>
            <div style={{ padding: "16px 20px" }}>
              {/* Key Details Grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
                {[
                  { label: "Date & time", val: `${fmtDate(demoDetail.date)} · ${fmt12(demoDetail.time)}` },
                  { label: "Duration", val: `${demoDetail.duration || 45} min` },
                  { label: "Contact", val: demoContact(demoDetail) || "—" },
                  { label: "Host rep", val: demoDetail.host_rep_name || "Unassigned" },
                  { label: "Product focus", val: demoDetail.product || demoDetail.demo_type || "—" },
                  { label: "Status", val: demoDetail.status?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || "—" },
                  { label: "AI Score", val: getLeadScore(demoDetail) ? `${getLeadScore(demoDetail)}/100` : "—" },
                  { label: "Deal value", val: demoDetail.deal_value ? `$${Number(demoDetail.deal_value).toLocaleString()}` : "—" },
                ].map((f, i) => (
                  <div key={i} style={{ background: "#f9f9f9", borderRadius: 8, padding: "8px 12px" }}>
                    <div style={{ fontSize: 9, textTransform: "uppercase", color: "#888", letterSpacing: "0.3px" }}>{f.label}</div>
                    <div style={{ fontSize: 13, fontWeight: 500, marginTop: 2 }}>{f.val}</div>
                  </div>
                ))}
              </div>
              {/* Meeting Link */}
              {demoDetail.meeting_link && (
                <div style={{ background: "#E6F1FB", borderRadius: 8, padding: "10px 14px", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div><div style={{ fontSize: 11, fontWeight: 600, color: "#185FA5" }}>RingCentral Meeting</div><div style={{ fontSize: 11, color: "#2563eb" }}>{demoDetail.meeting_link}</div></div>
                  <button onClick={() => { navigator.clipboard.writeText(demoDetail.meeting_link); toast.success("Link copied"); }} style={{ border: "0.5px solid #B5D4F4", borderRadius: 6, padding: "4px 10px", fontSize: 11, background: "#fff", cursor: "pointer", fontFamily: "inherit", color: "#185FA5" }}>Copy</button>
                </div>
              )}
              {/* Internal Notes */}
              {demoDetail.internal_notes && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ ...SL, marginBottom: 4 }}>Internal notes</div>
                  <div style={{ fontSize: 12, color: "#888", background: "#f9f9f9", borderRadius: 8, padding: "8px 12px" }}>{demoDetail.internal_notes}</div>
                </div>
              )}
              {/* Action buttons */}
              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                {demoDetail.lead_id && <button data-testid="demo-detail-open-record" onClick={() => { setDemoDetail(null); openRecord(demoDetail); }} style={{ flex: 1, border: "0.5px solid #ddd", borderRadius: 8, padding: "8px 14px", fontSize: 12, cursor: "pointer", fontFamily: "inherit", background: "#fff" }}>{isClient ? "View Client" : "View full lead profile"}</button>}
                {demoDetail.status === "confirmed" && <button onClick={() => { setDemoDetail(null); setActiveTab("live-meeting"); setLiveMeetingDemo(demoDetail); }} className="btn btn-primary" style={{ flex: 1, fontSize: 12 }} data-testid="detail-join-meeting"><Phone style={{ width: 12, height: 12 }} /> Join meeting</button>}
                {(demoDetail.status === "confirmed" || demoDetail.status === "completed") && <button onClick={() => { setDemoDetail(null); setShowOutcome(demoDetail); }} style={{ flex: 1, border: "0.5px solid #CECBF6", color: "#3C3489", borderRadius: 8, padding: "8px 14px", fontSize: 12, cursor: "pointer", fontFamily: "inherit", background: "#fff" }}>Log outcome</button>}
                <button onClick={() => { const d = demoDetail; setDemoDetail(null); setShowReschedule(d); }} disabled={!rescheduleEligible(demoDetail)} data-testid="detail-reschedule" style={{ flex: 1, border: "0.5px solid #E4C58A", color: "#854F0B", borderRadius: 8, padding: "8px 14px", fontSize: 12, cursor: rescheduleEligible(demoDetail) ? "pointer" : "not-allowed", fontFamily: "inherit", background: "#fff", opacity: rescheduleEligible(demoDetail) ? 1 : 0.45 }}><Clock style={{ width: 12, height: 12, display: "inline", verticalAlign: "middle", marginRight: 4 }} />Reschedule</button>
              </div>
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// Phase C (Iter 135) — per-rep Outlook → CRM webhook subscription button.
// The toggle above (Phase A) controls outbound (CRM → Outlook). This
// component controls INBOUND (Outlook → CRM) and is per-user because
// each rep's calendar needs its own Graph subscription.
function OutlookInboundSubscribeButton() {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/integrations/m365/calendar/subscription`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (alive) setStatus(r.data);
      } catch { /* not configured */ }
    })();
    return () => { alive = false; };
  }, [token]);

  const subscribe = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/integrations/m365/calendar/subscribe`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus({ subscribed: true, subscription: r.data });
      toast.success("Outlook → CRM sync is now ON for your account. New calendar events flow into the CRM automatically.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Subscribe failed — make sure M365 is connected first.");
    } finally { setBusy(false); }
  };
  const unsubscribe = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/integrations/m365/calendar/unsubscribe`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus({ subscribed: false, subscription: null });
      toast.success("Outlook → CRM sync turned off for your account.");
    } catch (e) {
      toast.error("Couldn't unsubscribe");
    } finally { setBusy(false); }
  };

  const subscribed = !!status?.subscribed;
  return (
    <div data-testid="outlook-inbound-subscribe-row" style={{ marginTop: 14, padding: 12, background: "#F8FAFC", border: "0.5px solid #E2E8F0", borderRadius: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
            Inbound — Outlook calendar → CRM (your account)
          </div>
          <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 3, lineHeight: 1.5 }}>
            When ON for your account, any meeting you create or edit directly in Outlook is mirrored into the CRM Meetings tab. Events matching a known lead's email auto-link to that lead; others land in the Meetings list with an "outlook_webhook" source tag.
          </div>
          {subscribed && status?.subscription?.expiration && (
            <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 6, fontStyle: "italic" }}>
              Subscription renews automatically. Currently expires {new Date(status.subscription.expiration).toLocaleString()}.
            </div>
          )}
        </div>
        <button
          data-testid={subscribed ? "outlook-inbound-unsubscribe" : "outlook-inbound-subscribe"}
          onClick={subscribed ? unsubscribe : subscribe}
          disabled={busy}
          style={{
            background: subscribed ? "#fff" : "#2563eb",
            color: subscribed ? "#dc2626" : "#fff",
            border: subscribed ? "0.5px solid #fca5a5" : "0.5px solid #2563eb",
            borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 600,
            cursor: busy ? "wait" : "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
          }}
        >
          {busy ? "…" : subscribed ? "Turn OFF for me" : "Turn ON for me"}
        </button>
      </div>
    </div>
  );
}


// Iter 175.4 — admin-only bulk-subscribe row. Calls
// /api/admin/integrations/m365/calendar/subscribe-all to push a Graph
// subscription on every rep who has M365 connected. Shows the per-user
// result breakdown so the admin can see who's missing tokens or hit a
// Graph error without grepping logs.
function OutlookAdminBulkSubscribeRow() {
  const { token, user } = useAuth();
  const role = String(user?.role || "").toLowerCase();
  const isAdmin = !!user?.is_admin || ["admin", "super_admin", "crm_admin"].includes(role);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  if (!isAdmin) return null;

  const runBulk = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/integrations/m365/calendar/subscribe-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 },
      );
      setLastResult(r.data);
      const c = r.data?.counts || {};
      toast.success(`Subscribed ${c.subscribed || 0} · already ${c.already || 0} · no tokens ${c.no_tokens || 0} · failed ${c.failed || 0}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk subscribe failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="outlook-admin-bulk-subscribe-row"
      style={{ marginTop: 10, padding: 12, background: "#FFFBEB", border: "0.5px solid #fde68a", borderRadius: 8 }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#92400e" }}>Admin · bulk-subscribe entire team</div>
          <div style={{ fontSize: 11.5, color: "#854d0e", marginTop: 3, lineHeight: 1.5 }}>
            One-click: pushes a Graph calendar push subscription for every rep with M365 connected. Skips reps who are already subscribed. Use after a deploy / env-var change that invalidated existing subscriptions, or to fix the &quot;0 active subscriptions&quot; state.
          </div>
        </div>
        <button
          type="button"
          data-testid="outlook-admin-bulk-subscribe-btn"
          onClick={runBulk}
          disabled={busy}
          style={{
            background: busy ? "#fde68a" : "#92400e",
            color: "#fff",
            border: "0.5px solid #92400e",
            borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 600,
            cursor: busy ? "wait" : "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
          }}
        >{busy ? "Running…" : "Subscribe all reps"}</button>
      </div>
      {lastResult && (
        <div
          data-testid="outlook-admin-bulk-subscribe-result"
          style={{ marginTop: 10, padding: 8, background: "#fff", borderRadius: 6, border: "0.5px solid #fde68a", fontSize: 11, color: "#374151", maxHeight: 220, overflowY: "auto" }}
        >
          {(lastResult.results || []).map((r) => (
            <div key={r.user_id} style={{ display: "flex", gap: 8, padding: "3px 0", borderBottom: "0.5px solid #f3f4f6" }}>
              <span style={{
                minWidth: 76, fontWeight: 600, color:
                  r.status === "subscribed" ? "#16a34a" :
                  r.status === "already_subscribed" ? "#0891b2" :
                  r.status === "no_tokens" ? "#94a3b8" : "#dc2626",
              }}>{r.status}</span>
              <span style={{ flex: 1 }}>{r.email}</span>
              {r.expiration && <span style={{ color: "#64748b" }}>exp {new Date(r.expiration).toLocaleDateString()}</span>}
              {r.error && <span style={{ color: "#dc2626", fontStyle: "italic" }} title={r.error}>error</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

